import React, { useState, useCallback, useEffect, useMemo } from "react";
import { usePricingCatalog } from "@/hooks/usePricingCatalog";

// ── Types ─────────────────────────────────────────────────────────────────

type ContractTerm = "monthly" | "annual" | "2year" | "3year";
type JourneyLevel = "L1" | "L2" | "L3";
type CommercePlatform = "bigcommerce" | "shopify" | "woocommerce" | "magento" | "square" | "none";

type Vertical =
  | "Casino/Gaming"
  | "E-commerce"
  | "Healthcare"
  | "Manufacturing"
  | "Staffing / HR Tech"
  | "SaaS"
  | "Financial Services"
  | "Hospitality"
  | "Government"
  | "Other";

interface CustomerInfo {
  companyName: string;
  contactName: string;
  contactEmail: string;
  vertical: Vertical | "";
}

type IvrTier = "essentials" | "professional" | "business" | "enterprise";

interface VolumeDiscovery {
  monthlyConversations: number;
  monthlyCallsIfIvr: number;
  monthlyBridgeMinutes: number;
  monthlyOutboundMinutes: number;
  monthlyWebVisitors: number;
  monthlyEmails: number;
  peakMonthMultiplier: number;
}

interface Channels {
  webChat: boolean;
  email: boolean;
  sms: boolean;
  whatsapp: boolean;
  ivr: boolean;
}

interface AddOns {
  languagePack: 0 | 1 | 3 | 5;
  hipaa: boolean;
  pci: boolean;
  dataResidency: boolean;
  ivrTier: IvrTier;  // which IVR package tier (Essentials/Professional/Business/Enterprise)
  smsBundle: boolean;
  whatsappBundle: boolean;
  standardApi: boolean;
  preBuiltIntegrationCount: number;
  complexIntegrationCount: number;
  // AI Compose Email add-on (included in Professional+; available as $149/mo add-on on Starter)
  aiEmailAddOn: boolean;
  // Recruiting Bot add-ons
  recruitingBot: boolean;
  recruitingBotAtsIntegration: boolean;  // private ATS API (SF OData / Workday REST)
  recruitingBotGlobalHiring: boolean;    // multi-country / multi-language premium
  recruitingBotEeoc: boolean;            // EEOC/EEO voluntary self-ID + reporting module
  recruitingBotCalendarScreening: boolean; // interview scheduling via Calendly/Outlook
}

interface CommerceConfig {
  platform: CommercePlatform;
  corePackage: boolean;       // 10 skills — included with integration
  commercePackage: boolean;   // +10 skills — cart recovery, checkout, intelligence
  b2bOpsPackage: boolean;     // +8 skills — B2B quotes, group pricing, operations
  logisticsPackage: boolean;  // +5 skills — shipping, gifting, order history
  agentSeats: number;         // live agent seats for this deal
  botConversationsPerMonth: number;  // monthly AI bot conversations
  skuCount: number;           // products to index
  storefrontCount: number;    // multi-storefront (e.g. ETF + Perform Better)
  pilotMonths: number;        // free pilot period (0 = no pilot)
  existingContractBase: number; // existing monthly base if upselling (e.g. Tracy = $1,444)
  perAgentRate: number;       // per-agent rate for existing customers
}

interface KnowledgeConfig {
  kbArticleCount: number;          // KB/help articles the bot references
  scraperPageCount: number;        // website pages to crawl per month (1 page = 1 URL crawled)
  pdfPageCount: number;            // uploaded file pages per month (1 page = 1 document page)
  hasPdfUploads: boolean;          // PDF/Word/CSV document indexing add-on
  hasOneDriveIntegration: boolean; // OneDrive/SharePoint auto-sync add-on
}

interface WizardState {
  step: number;
  customer: CustomerInfo;
  volume: VolumeDiscovery;
  channels: Channels;
  journey: { level: JourneyLevel; integrationCount: number };
  commerce: CommerceConfig;
  addons: AddOns;
  knowledge: KnowledgeConfig;
  contractTerm: ContractTerm;
  l3Orchestration: boolean;
  extraConversationBlocks: number;
  // Enterprise modular add-on bundles (stacks on top of plan baseline)
  extraWorkflowAiBlocks: number;
  extraMcpBlocks: number;
  extraSmsBlocks: number;
  extraIvrBlocks: number;
}

interface LineItem {
  label: string;
  amount: number;
  note?: string;
  isPassThrough?: boolean;
  isWarning?: boolean;
}

interface QuoteResult {
  lines: LineItem[];
  monthlySubtotal: number;
  monthlyAfterDiscount: number;
  annualAfterDiscount: number;
  setupFee: number;
  year1Total: number;
  discountPct: number;
  contractDiscountPct: number;
  ivrFloorWarning: boolean;
  ivrFloorShortfall: number;
  tierName: string;
  tierConversations: number;
}

interface FlagNote {
  note: string;
  savedAt: string;
}

// ── Pricing constants ─────────────────────────────────────────────────────
// SOURCE OF TRUTH: GET /Pricing/catalog (PricingCatalogSeeder.cs in velaro-admin).
// Edit prices in PricingCatalogSeeder.cs — NOT here. These are fallback defaults only,
// used while the catalog loads. The hook below overwrites them on first render.

// Conversation counts MUST match SiteProvisioningService.CreateSubscriptionForTier() in velaro-messaging.
const TIERS = [
  { name: "Starter",      monthly: 497,   conversations: 1000,  label: "Starter — $497/mo" },
  { name: "Professional", monthly: 1497,  conversations: 5000,  label: "Professional — $1,497/mo" },
  { name: "Enterprise",   monthly: 7997,  conversations: 25000, label: "Enterprise — custom pricing" },
];

// ── Per-tier AI & automation hard limits ─────────────────────────────────
// These must match SiteProvisioningService.CreateSubscriptionForTier() in velaro-messaging.
// Enforced with OverageBehavior="block" — customers hitting these are hard-stopped, not warned.
// Update both places when changing limits.
const PLAN_AI_LIMITS: Record<string, {
  workflowAiCalls: number;
  mcpCalls: number | "n/a";
  workflowRuns: number;
  conversations: number;
  aiTokensM: number;  // millions
}> = {
  Starter:      { workflowAiCalls: 200,    mcpCalls: "n/a",    workflowRuns: 1_000,  conversations: 1_000,  aiTokensM: 0.5 },
  Professional: { workflowAiCalls: 1_000,  mcpCalls: 5_000,    workflowRuns: 10_000, conversations: 5_000,  aiTokensM: 5   },
  Enterprise:   { workflowAiCalls: 10_000, mcpCalls: 250_000,  workflowRuns: 1_000_000, conversations: 25_000, aiTokensM: 100 },
};

const CONTRACT_DISCOUNTS: Record<ContractTerm, number> = {
  monthly: 0,
  annual:  0.10,
  "2year": 0.20,
  "3year": 0.25,
};

const CONTRACT_LABELS: Record<ContractTerm, string> = {
  monthly: "Monthly (no discount)",
  annual:  "Annual — 10% off",
  "2year": "2-Year — 20% off",
  "3year": "3-Year — 25% off",
};

const LANGUAGE_PACK_PRICES: Record<number, number> = {
  0: 0, 1: 300, 3: 600, 5: 900,
};

// IVR tiered pricing — multilingual + live transfer + outbound now gated by tier, not separate add-ons.
// ElevenLabs eleven_turbo_v2_5 cost: $0.000030/char (~$0.006/response avg) — negligible vs Twilio.
// Twilio inbound: $0.0085/min | ConversationRelay: $0.07/min | Bridge PSTN: $0.013/min | Outbound: $0.013/min
const IVR_TIERS: Record<IvrTier, { name: string; price: number; includedMin: number; includedBridgeMin: number; languages: number; liveTransfer: boolean; outbound: boolean; screenPop: boolean; minPlan: string; blurb: string }> = {
  essentials:   { name: "IVR Essentials",   price: 499,  includedMin: 1000,  includedBridgeMin: 0,    languages: 1, liveTransfer: false, outbound: false, screenPop: false, minPlan: "Starter+",     blurb: "Bot-only · English · Basic routing" },
  professional: { name: "IVR Professional", price: 1299, includedMin: 3000,  includedBridgeMin: 100,  languages: 2, liveTransfer: true,  outbound: false, screenPop: false, minPlan: "Professional+", blurb: "Live transfer · Eng+1 lang · Callback · Analytics" },
  business:     { name: "IVR Business",     price: 2499, includedMin: 7500,  includedBridgeMin: 500,  languages: 3, liveTransfer: true,  outbound: false, screenPop: true,  minPlan: "Professional+", blurb: "CRM screen pop · Multilingual 3 · Supervisor monitor" },
  enterprise:   { name: "IVR Enterprise",   price: 3700, includedMin: 10000, includedBridgeMin: 1000, languages: 5, liveTransfer: true,  outbound: true,  screenPop: true,  minPlan: "Enterprise",    blurb: "Outbound campaigns · Multilingual 5 · Custom voice · SLA" },
};
// Bridge overage: AI off during PSTN transfer. Cost ~$0.0215/min, charge $0.20/min = 9x margin.
const IVR_BRIDGE_BLOCK_SIZE  = 1000;
const IVR_BRIDGE_BLOCK_PRICE = 200;
// Outbound AI campaign (Enterprise only): cost ~$0.083/min, charge $0.20/min = 2.4x margin.
const IVR_OUTBOUND_BLOCK_SIZE  = 2500;
const IVR_OUTBOUND_BLOCK_PRICE = 500;
// Extra inbound AI minutes above tier included: cost ~$0.079/min, charge $0.05/min (block = $250).
const IVR_EXTRA_MIN_BLOCK_SIZE  = 5000;
const IVR_EXTRA_MIN_BLOCK_PRICE = 250;
// Legacy aliases used by floor warning
const IVR_FLOOR        = IVR_TIERS.enterprise.price;
const IVR_MINUTE_BLOCK = IVR_EXTRA_MIN_BLOCK_PRICE;
// CONVERSATION DEFINITION (canonical — must match Subscription.cs comment and all marketing copy):
// One conversation = one continuous visitor session from first message until either:
//   (a) 30 minutes of inactivity, OR (b) bot/agent explicitly closes/resolves it.
// A visitor returning after 30 min starts a NEW conversation. Message count is irrelevant.
// A 3-exchange session and a 30-exchange session both count as 1 conversation.
const CONVERSATION_BLOCK_SIZE = 2500;   // convos per add-on block
const CONVERSATION_BLOCK_PRICE = 375;   // $375/block = $0.15/convo to customer; Velaro cost ~$0.014/convo (Sonnet)

// ── Enterprise modular add-on bundles ─────────────────────────────────────
// These can be stacked — enterprise contracts are custom, these are the list prices.
const ENT_WORKFLOW_AI_BLOCK_SIZE  = 5_000;   // extra workflow AI handoffs/mo
const ENT_WORKFLOW_AI_BLOCK_PRICE = 500;     // $0.10/call — above the plan baseline
const ENT_MCP_BLOCK_SIZE          = 25_000;  // extra MCP skill calls/mo
const ENT_MCP_BLOCK_PRICE         = 400;     // $0.016/call — customers pay their own LLM costs on top
const ENT_SMS_BLOCK_SIZE          = 2_000;   // extra outbound SMS/mo (Twilio ~$0.008/msg)
const ENT_SMS_BLOCK_PRICE         = 75;      // $0.0375/msg — healthy margin over Twilio pass-through
const ENT_IVR_BLOCK_SIZE          = 500;     // extra IVR minutes/mo
const ENT_IVR_BLOCK_PRICE         = 100;     // $0.20/min — Twilio ~$0.013/min, covers overhead + TTS
const SETUP_FEE_PCT = 0.20;
const SETUP_FEE_MIN = 3000;

// ── AI Compose Email Add-on Pricing ──────────────────────────────────────
// Included in Professional+ tiers (EnableAiComposedEmails = true in PackageVersion).
// Available as a standalone add-on for Starter sites: $149/mo.
// 200 AI-composed emails/day included; burst capacity priced per usage above limit.
const AI_EMAIL_ADDON_PRICE = 149;

// ── Recruiting Bot Add-on Pricing ────────────────────────────────────────
// Competing with Paradox enterprise contracts ($150K–$250K/yr for recruiting only).
// These are monthly add-on prices on top of the base platform.
const RECRUITING_BOT_BASE      = 500;   // Base recruiting bot module
const RECRUITING_ATS_PRICE     = 400;   // Private ATS API integration (SF OData / Workday REST)
const RECRUITING_GLOBAL_PRICE  = 600;   // Multi-country / global hiring premium (multi-language feed support)
const RECRUITING_EEOC_PRICE    = 300;   // EEOC/EEO voluntary self-ID collection + CSV report export
const RECRUITING_CALENDAR_PRICE = 250;  // Interview scheduling via Calendly/Outlook (reuses existing calendar booking)

// ── Commerce Skill Package Pricing ───────────────────────────────────────
// These are add-on prices for marketplace/direct customers.
// Marketplace plans (Shopify/BigCommerce App Store) bundle packages into tiers.
// Direct sales can mix and match packages as add-ons.
// "Retail" prices shown to customers; "marked down" prices for loyal/pilot customers.

const COMMERCE_PACKAGES = {
  core:       { name: "Core",          skills: 10, retail: 500, marked: 250, desc: "Product search, stock, orders, tracking, customer lookup, categories, reviews, variants, store config" },
  commerce:   { name: "Commerce",      skills: 10, retail: 1000, marked: 500, desc: "Cart builder, abandoned cart recovery, coupons, recommendations, compare, customer intelligence, brand browse, wishlist" },
  b2bOps:     { name: "B2B & Ops",     skills: 8,  retail: 1000, marked: 500, desc: "Group pricing, price lists, B2B quotes, customer details, cancel order, refunds, invoices, order notes" },
  logistics:  { name: "Logistics",     skills: 5,  retail: 500,  marked: 250, desc: "Shipping methods, gift certificates, customer orders, create coupon, price rules" },
} as const;

const COMMERCE_ALL_IN_RETAIL = 2500;  // all 33 skills retail
const COMMERCE_ALL_IN_MARKED = 1200;  // all 33 skills marked down

const COMMERCE_PLATFORM_LABELS: Record<CommercePlatform, string> = {
  bigcommerce: "BigCommerce",
  shopify:     "Shopify",
  woocommerce: "WooCommerce",
  magento:     "Magento / Adobe Commerce",
  square:      "Square",
  none:        "None",
};

const COMMERCE_PLATFORM_SKILLS: Record<CommercePlatform, number> = {
  bigcommerce: 33, shopify: 35, woocommerce: 28, magento: 29, square: 28, none: 0,
};

// Bot conversation pricing — per-1K-conversation blocks
const BOT_CONVERSATION_BLOCK_SIZE = 1000;
const BOT_CONVERSATION_BLOCK_PRICE = 150;  // $150 per 1K bot conversations/month

// Knowledge & AI Index add-on pricing — CUMULATIVE tiers
// Basic plan includes 200 articles + 500 pages/mo; Pro 1,000 + 5,000; Enterprise 5,000 + 50,000.
// Each threshold crossed adds an incremental charge (both stack at Enterprise level).
//   Pro KB:       201–1,000 articles     → +$100/mo
//   Ent KB:       1,001+ articles        → +$150/mo on top of Pro KB = $250/mo total
//   Pro Crawl:    501–5,000 pages/mo     → +$100/mo
//   Ent Crawl:    5,001+ pages/mo        → +$150/mo on top of Pro Crawl = $250/mo total
const KB_ARTICLE_PRO_THRESHOLD = 200;
const KB_ARTICLE_ENT_THRESHOLD = 1000;
const KB_ARTICLE_PRO_PRICE = 100;   // 201–1,000 articles
const KB_ARTICLE_ENT_PRICE = 150;   // 1,001+ articles (stacks on top of Pro)
const SCRAPER_PAGE_PRO_THRESHOLD = 500;
const SCRAPER_PAGE_ENT_THRESHOLD = 5000;
const SCRAPER_PAGE_PRO_PRICE = 100;  // 501–5,000 pages/mo
const SCRAPER_PAGE_ENT_PRICE = 150;  // 5,001+ pages/mo (stacks on top of Pro)
const PDF_INDEXING_PRICE = 150;
const ONEDRIVE_SYNC_PRICE = 200;

type KnowledgeTier = "Basic" | "Pro" | "Enterprise";

// pdfPageCount = estimated uploaded document pages per month.
// 1 page = 1 document page (PDF, DOCX, XLSX). Scraper pages are separate.
// Plan limits: Starter 500/mo (max 200/file), Pro 5,000/mo (max 500/file), Enterprise 50,000/mo (max 2,000/file).
const KNOWLEDGE_VERTICAL_DEFAULTS: Record<Vertical, KnowledgeConfig> = {
  "E-commerce":         { kbArticleCount: 80,  scraperPageCount: 2000, pdfPageCount: 0,    hasPdfUploads: false, hasOneDriveIntegration: false },
  "Healthcare":         { kbArticleCount: 400, scraperPageCount: 800,  pdfPageCount: 2000, hasPdfUploads: true,  hasOneDriveIntegration: false },
  "Manufacturing":      { kbArticleCount: 250, scraperPageCount: 1500, pdfPageCount: 3000, hasPdfUploads: true,  hasOneDriveIntegration: true  },
  "Staffing / HR Tech": { kbArticleCount: 200, scraperPageCount: 1000, pdfPageCount: 500,  hasPdfUploads: false, hasOneDriveIntegration: false },
  "SaaS":               { kbArticleCount: 600, scraperPageCount: 1200, pdfPageCount: 1000, hasPdfUploads: true,  hasOneDriveIntegration: true  },
  "Financial Services": { kbArticleCount: 350, scraperPageCount: 600,  pdfPageCount: 5000, hasPdfUploads: true,  hasOneDriveIntegration: false },
  "Hospitality":        { kbArticleCount: 150, scraperPageCount: 1000, pdfPageCount: 0,    hasPdfUploads: false, hasOneDriveIntegration: false },
  "Government":         { kbArticleCount: 500, scraperPageCount: 2500, pdfPageCount: 8000, hasPdfUploads: true,  hasOneDriveIntegration: true  },
  "Casino/Gaming":      { kbArticleCount: 120, scraperPageCount: 3000, pdfPageCount: 0,    hasPdfUploads: false, hasOneDriveIntegration: false },
  "Other":              { kbArticleCount: 100, scraperPageCount: 1000, pdfPageCount: 0,    hasPdfUploads: false, hasOneDriveIntegration: false },
};

const KNOWLEDGE_PLATFORM_PAGE_BOOST: Partial<Record<CommercePlatform, number>> = {
  bigcommerce: 2000, shopify: 1500, woocommerce: 1000, magento: 3000, square: 500,
};

const KNOWLEDGE_TIER_COLORS: Record<KnowledgeTier, string> = {
  Basic: "text-green-400", Pro: "text-blue-400", Enterprise: "text-purple-400",
};
const KNOWLEDGE_TIER_BORDERS: Record<KnowledgeTier, string> = {
  Basic: "border-green-700 bg-green-900/20",
  Pro:   "border-blue-700 bg-blue-900/20",
  Enterprise: "border-purple-700 bg-purple-900/20",
};

// ── Knowledge helpers ──────────────────────────────────────────────────────

function getKnowledgeTier(count: number, proThreshold: number, entThreshold: number): KnowledgeTier {
  return count > entThreshold ? "Enterprise" : count > proThreshold ? "Pro" : "Basic";
}

function getKnowledgeDefaults(vertical: Vertical | "", platform: CommercePlatform): KnowledgeConfig {
  const base: KnowledgeConfig = { kbArticleCount: 0, scraperPageCount: 0, pdfPageCount: 0, hasPdfUploads: false, hasOneDriveIntegration: false };
  if (!vertical) return base;
  const d = KNOWLEDGE_VERTICAL_DEFAULTS[vertical] ?? base;
  const platformPages = platform !== "none" ? (KNOWLEDGE_PLATFORM_PAGE_BOOST[platform] ?? 0) : 0;
  return { ...d, scraperPageCount: Math.max(d.scraperPageCount, platformPages) };
}

// ── Tier selection logic ───────────────────────────────────────────────────

function selectTier(monthlyConversations: number) {
  if (monthlyConversations <= 1000)  return TIERS[0]; // Starter
  if (monthlyConversations <= 5000)  return TIERS[1]; // Professional
  return TIERS[2];                                     // Enterprise
}

function calcIvrExtraMinutes(monthlyMin: number, included: number) {
  if (monthlyMin <= included) return { blocks: 0, cost: 0, label: "" };
  const blocks = Math.ceil((monthlyMin - included) / IVR_EXTRA_MIN_BLOCK_SIZE);
  return { blocks, cost: blocks * IVR_EXTRA_MIN_BLOCK_PRICE, label: `Extra IVR AI minutes (+${(blocks * IVR_EXTRA_MIN_BLOCK_SIZE).toLocaleString()} min/mo, ${blocks} block${blocks > 1 ? "s" : ""})` };
}
function calcBridgeMinutes(monthlyBridge: number, included: number) {
  if (monthlyBridge <= included) return { blocks: 0, cost: 0, label: "" };
  const blocks = Math.ceil((monthlyBridge - included) / IVR_BRIDGE_BLOCK_SIZE);
  return { blocks, cost: blocks * IVR_BRIDGE_BLOCK_PRICE, label: `Bridge overage (+${(blocks * IVR_BRIDGE_BLOCK_SIZE).toLocaleString()} min/mo, ${blocks} block${blocks > 1 ? "s" : ""})` };
}
function calcOutboundMinutes(monthlyOutbound: number) {
  if (monthlyOutbound <= 0) return { blocks: 0, cost: 0, label: "" };
  const blocks = Math.ceil(monthlyOutbound / IVR_OUTBOUND_BLOCK_SIZE);
  return { blocks, cost: blocks * IVR_OUTBOUND_BLOCK_PRICE, label: `Outbound AI campaign minutes (+${(blocks * IVR_OUTBOUND_BLOCK_SIZE).toLocaleString()} min/mo, ${blocks} block${blocks > 1 ? "s" : ""})` };
}

// ── Quote calculator ───────────────────────────────────────────────────────

function calculateQuote(state: WizardState): QuoteResult {
  const { volume, channels, addons, journey, contractTerm, l3Orchestration,
          extraConversationBlocks, extraWorkflowAiBlocks, extraMcpBlocks,
          extraSmsBlocks, extraIvrBlocks } = state;
  const lines: LineItem[] = [];

  // Platform tier
  const tier = selectTier(volume.monthlyConversations);
  lines.push({ label: `Platform — ${tier.name} tier`, amount: tier.monthly });

  // Extra conversation blocks
  if (extraConversationBlocks > 0) {
    const blockCost = extraConversationBlocks * CONVERSATION_BLOCK_PRICE;
    lines.push({
      label: `Extra conversation blocks (${extraConversationBlocks} × 2,500)`,
      amount: blockCost,
      note: `+${(extraConversationBlocks * CONVERSATION_BLOCK_SIZE).toLocaleString()} conversations/mo`,
    });
  }

  // Enterprise modular add-on bundles
  if (extraWorkflowAiBlocks > 0) {
    lines.push({
      label: `Workflow AI bundle (${extraWorkflowAiBlocks} × ${ENT_WORKFLOW_AI_BLOCK_SIZE.toLocaleString()} calls)`,
      amount: extraWorkflowAiBlocks * ENT_WORKFLOW_AI_BLOCK_PRICE,
      note: `+${(extraWorkflowAiBlocks * ENT_WORKFLOW_AI_BLOCK_SIZE).toLocaleString()} workflow AI handoffs/mo`,
    });
  }
  if (extraMcpBlocks > 0) {
    lines.push({
      label: `MCP skill call bundle (${extraMcpBlocks} × ${ENT_MCP_BLOCK_SIZE.toLocaleString()} calls)`,
      amount: extraMcpBlocks * ENT_MCP_BLOCK_PRICE,
      note: `+${(extraMcpBlocks * ENT_MCP_BLOCK_SIZE).toLocaleString()} MCP skill calls/mo`,
    });
  }
  if (extraSmsBlocks > 0) {
    lines.push({
      label: `SMS bundle (${extraSmsBlocks} × ${ENT_SMS_BLOCK_SIZE.toLocaleString()} messages)`,
      amount: extraSmsBlocks * ENT_SMS_BLOCK_PRICE,
      note: `+${(extraSmsBlocks * ENT_SMS_BLOCK_SIZE).toLocaleString()} outbound SMS/mo`,
    });
  }
  if (extraIvrBlocks > 0) {
    lines.push({
      label: `IVR minute bundle (${extraIvrBlocks} × ${ENT_IVR_BLOCK_SIZE} minutes)`,
      amount: extraIvrBlocks * ENT_IVR_BLOCK_PRICE,
      note: `+${(extraIvrBlocks * ENT_IVR_BLOCK_SIZE).toLocaleString()} IVR minutes/mo`,
    });
  }

  // IVR add-ons
  if (channels.ivr) {
    const tier = IVR_TIERS[addons.ivrTier];
    lines.push({ label: `${tier.name}`, amount: tier.price, note: `${tier.includedMin.toLocaleString()} AI min/mo included · ${tier.blurb}` });

    const extraMin = calcIvrExtraMinutes(volume.monthlyCallsIfIvr, tier.includedMin);
    if (extraMin.cost > 0) lines.push({ label: extraMin.label, amount: extraMin.cost, note: `~${volume.monthlyCallsIfIvr.toLocaleString()} min/mo total` });

    if (tier.liveTransfer) {
      const bridge = calcBridgeMinutes(volume.monthlyBridgeMinutes, tier.includedBridgeMin);
      if (tier.includedBridgeMin > 0 && volume.monthlyBridgeMinutes === 0) {
        lines.push({ label: `Bridge minutes included`, amount: 0, note: `${tier.includedBridgeMin.toLocaleString()} min/mo included — overage $${IVR_BRIDGE_BLOCK_PRICE}/${IVR_BRIDGE_BLOCK_SIZE.toLocaleString()} min` });
      } else if (bridge.cost > 0) {
        lines.push({ label: bridge.label, amount: bridge.cost, note: `${volume.monthlyBridgeMinutes.toLocaleString()} bridge min total, ${tier.includedBridgeMin.toLocaleString()} included` });
      }
    }

    if (tier.outbound && volume.monthlyOutboundMinutes > 0) {
      const outbound = calcOutboundMinutes(volume.monthlyOutboundMinutes);
      if (outbound.cost > 0) lines.push({ label: outbound.label, amount: outbound.cost, note: `$${IVR_OUTBOUND_BLOCK_PRICE}/${IVR_OUTBOUND_BLOCK_SIZE.toLocaleString()} min block` });
    }
  }

  // SMS
  if (channels.sms || addons.smsBundle) {
    lines.push({ label: "SMS Bundle 5K", amount: 500 });
  }

  // WhatsApp
  if (channels.whatsapp || addons.whatsappBundle) {
    lines.push({ label: "WhatsApp Channel", amount: 500 });
  }

  // L3 orchestration
  if (l3Orchestration || journey.level === "L3") {
    lines.push({ label: "L3 AI Orchestration Upgrade", amount: 1500 });
  }

  // Language packs
  if (addons.languagePack > 0) {
    const lpCost = LANGUAGE_PACK_PRICES[addons.languagePack] ?? 0;
    lines.push({ label: `Language Pack (+${addons.languagePack} language${addons.languagePack > 1 ? "s" : ""})`, amount: lpCost });
  }

  // Compliance
  if (addons.hipaa)         lines.push({ label: "HIPAA Compliance",              amount: 2000 });
  if (addons.pci)           lines.push({ label: "PCI DSS Compliance",             amount: 1500 });
  if (addons.dataResidency) lines.push({ label: "Single-Region Data Residency",   amount: 500 });

  // AI Compose Email (Starter add-on only — Professional+ includes it)
  if (addons.aiEmailAddOn) {
    const tier = TIERS.find(t => t.conversations >= state.volume.monthlyConversations) ?? TIERS[TIERS.length - 1];
    const includedInTier = tier.name !== "Starter";
    if (!includedInTier) {
      lines.push({ label: "AI Compose Email Add-on (200 AI-written emails/day)", amount: AI_EMAIL_ADDON_PRICE, note: "Included in Professional+ at no extra charge" });
    }
  }

  // Recruiting Bot
  if (addons.recruitingBot) {
    lines.push({ label: "Recruiting Bot — Candidate Intake AI", amount: RECRUITING_BOT_BASE, note: "vs. Paradox $12K–$20K/mo" });
    if (addons.recruitingBotAtsIntegration)     lines.push({ label: "ATS Integration (SuccessFactors / Workday OData)", amount: RECRUITING_ATS_PRICE });
    if (addons.recruitingBotGlobalHiring)        lines.push({ label: "Global Hiring Premium (multi-country, multi-language feed)", amount: RECRUITING_GLOBAL_PRICE });
    if (addons.recruitingBotEeoc)                lines.push({ label: "EEOC/EEO Compliance Module (voluntary self-ID + report export)", amount: RECRUITING_EEOC_PRICE });
    if (addons.recruitingBotCalendarScreening)   lines.push({ label: "Interview Scheduling (Calendly / Outlook — auto-book screens)", amount: RECRUITING_CALENDAR_PRICE });
  }

  // Integrations
  if (addons.preBuiltIntegrationCount > 0) {
    const cost = addons.preBuiltIntegrationCount * 299; // mid-range of $99-500
    lines.push({ label: `Pre-built Integrations (${addons.preBuiltIntegrationCount}×)`, amount: cost, note: "avg $299/mo each" });
  }
  if (addons.complexIntegrationCount > 0) {
    const cost = addons.complexIntegrationCount * 400; // mid-range of $299-500
    lines.push({ label: `Complex/Legacy Integrations (${addons.complexIntegrationCount}×)`, amount: cost, note: "avg $400/mo each" });
  }
  if (addons.standardApi) {
    lines.push({ label: "Standard API Integration", amount: 299 });
  }

  // Commerce Integration
  const { commerce } = state;
  if (commerce.platform !== "none") {
    const platformLabel = COMMERCE_PLATFORM_LABELS[commerce.platform];

    // Existing contract base (for upsell deals like MF Athletic)
    if (commerce.existingContractBase > 0) {
      lines.push({
        label: `Existing contract base (${commerce.agentSeats} agents @ ${fmt(commerce.perAgentRate)})`,
        amount: commerce.existingContractBase,
        note: "Current agreement — unchanged",
      });
    }

    // Skill packages
    if (commerce.corePackage) {
      lines.push({
        label: `${platformLabel} Core Package (${COMMERCE_PACKAGES.core.skills} skills)`,
        amount: COMMERCE_PACKAGES.core.marked,
        note: `Retail ${fmt(COMMERCE_PACKAGES.core.retail)} — marked down`,
      });
    }
    if (commerce.commercePackage) {
      lines.push({
        label: `${platformLabel} Commerce Package (+${COMMERCE_PACKAGES.commerce.skills} skills)`,
        amount: COMMERCE_PACKAGES.commerce.marked,
        note: `Retail ${fmt(COMMERCE_PACKAGES.commerce.retail)} — marked down`,
      });
    }
    if (commerce.b2bOpsPackage) {
      lines.push({
        label: `${platformLabel} B2B & Ops Package (+${COMMERCE_PACKAGES.b2bOps.skills} skills)`,
        amount: COMMERCE_PACKAGES.b2bOps.marked,
        note: `Retail ${fmt(COMMERCE_PACKAGES.b2bOps.retail)} — marked down`,
      });
    }
    if (commerce.logisticsPackage) {
      lines.push({
        label: `${platformLabel} Logistics Package (+${COMMERCE_PACKAGES.logistics.skills} skills)`,
        amount: COMMERCE_PACKAGES.logistics.marked,
        note: `Retail ${fmt(COMMERCE_PACKAGES.logistics.retail)} — marked down`,
      });
    }

    // All-in discount check
    const allSelected = commerce.corePackage && commerce.commercePackage && commerce.b2bOpsPackage && commerce.logisticsPackage;
    if (allSelected) {
      const packageTotal = COMMERCE_PACKAGES.core.marked + COMMERCE_PACKAGES.commerce.marked + COMMERCE_PACKAGES.b2bOps.marked + COMMERCE_PACKAGES.logistics.marked;
      const savings = packageTotal - COMMERCE_ALL_IN_MARKED;
      if (savings > 0) {
        lines.push({
          label: `All-In Bundle Discount (all ${COMMERCE_PLATFORM_SKILLS[commerce.platform]} skills)`,
          amount: -savings,
          note: `All-in ${fmt(COMMERCE_ALL_IN_MARKED)}/mo saves ${fmt(savings)}/mo`,
        });
      }
    }

    // Bot conversations
    if (commerce.botConversationsPerMonth > 0) {
      const blocks = Math.ceil(commerce.botConversationsPerMonth / BOT_CONVERSATION_BLOCK_SIZE);
      lines.push({
        label: `AI Bot Conversations (${commerce.botConversationsPerMonth.toLocaleString()}/mo)`,
        amount: blocks * BOT_CONVERSATION_BLOCK_PRICE,
        note: `${blocks} × 1K block @ ${fmt(BOT_CONVERSATION_BLOCK_PRICE)}/block`,
      });
    }

    // Product indexing (included up to 10K; $50/mo per additional 10K SKUs)
    if (commerce.skuCount > 10000) {
      const extraBlocks = Math.ceil((commerce.skuCount - 10000) / 10000);
      lines.push({
        label: `Product Index Expansion (${commerce.skuCount.toLocaleString()} SKUs)`,
        amount: extraBlocks * 50,
        note: `10K included, +${fmt(50)} per 10K additional`,
      });
    }

    // Multi-storefront
    if (commerce.storefrontCount > 1) {
      lines.push({
        label: `Multi-Storefront (${commerce.storefrontCount} stores)`,
        amount: (commerce.storefrontCount - 1) * 100,
        note: `1 included, +${fmt(100)}/mo per additional`,
      });
    }

    // Pilot discount
    if (commerce.pilotMonths > 0) {
      const commerceSubtotal = lines.filter(l => l.label.includes(platformLabel) || l.label.includes("All-In") || l.label.includes("Bot Conv") || l.label.includes("Product Index") || l.label.includes("Multi-Storefront")).reduce((s, l) => s + l.amount, 0);
      if (commerceSubtotal > 0) {
        lines.push({
          label: `${commerce.pilotMonths}-Month Pilot — commerce add-ons waived`,
          amount: 0,
          note: `${fmt(commerceSubtotal)}/mo waived for first ${commerce.pilotMonths} months`,
          isWarning: true,
        });
      }
    }
  }

  // Knowledge & AI Index add-ons
  const { knowledge } = state;

  if (knowledge.kbArticleCount > KB_ARTICLE_PRO_THRESHOLD) {
    lines.push({
      label: `Knowledge Base Pro (${KB_ARTICLE_PRO_THRESHOLD + 1}–${KB_ARTICLE_ENT_THRESHOLD.toLocaleString()} articles)`,
      amount: KB_ARTICLE_PRO_PRICE,
      note: `Pro KB tier — up to ${KB_ARTICLE_ENT_THRESHOLD.toLocaleString()} articles`,
    });
  }
  if (knowledge.kbArticleCount > KB_ARTICLE_ENT_THRESHOLD) {
    lines.push({
      label: `Knowledge Base Enterprise Upgrade (${knowledge.kbArticleCount.toLocaleString()} articles)`,
      amount: KB_ARTICLE_ENT_PRICE,
      note: `${KB_ARTICLE_ENT_THRESHOLD.toLocaleString()}+ articles — stacks on Pro KB`,
    });
  }

  if (knowledge.scraperPageCount > SCRAPER_PAGE_PRO_THRESHOLD) {
    lines.push({
      label: `Web Crawling Pro (${SCRAPER_PAGE_PRO_THRESHOLD + 1}–${SCRAPER_PAGE_ENT_THRESHOLD.toLocaleString()} pages/mo)`,
      amount: SCRAPER_PAGE_PRO_PRICE,
      note: `Pro crawl tier — up to ${SCRAPER_PAGE_ENT_THRESHOLD.toLocaleString()} pages/mo`,
    });
  }
  if (knowledge.scraperPageCount > SCRAPER_PAGE_ENT_THRESHOLD) {
    lines.push({
      label: `Web Crawling Enterprise Upgrade (${knowledge.scraperPageCount.toLocaleString()} pages/mo)`,
      amount: SCRAPER_PAGE_ENT_PRICE,
      note: `${SCRAPER_PAGE_ENT_THRESHOLD.toLocaleString()}+ pages/mo — stacks on Pro Crawl`,
    });
  }

  if (knowledge.hasPdfUploads) {
    lines.push({ label: "Advanced Document Indexing (PDF / Word / CSV)", amount: PDF_INDEXING_PRICE });
  }

  if (knowledge.hasOneDriveIntegration) {
    lines.push({ label: "OneDrive / SharePoint Auto-Sync", amount: ONEDRIVE_SYNC_PRICE });
  }

  // Totals
  const monthlySubtotal = lines.reduce((s, l) => s + l.amount, 0);
  const contractDiscountPct = CONTRACT_DISCOUNTS[contractTerm];
  const monthlyAfterDiscount = monthlySubtotal * (1 - contractDiscountPct);
  const annualAfterDiscount = monthlyAfterDiscount * 12;

  // Setup fee (20% of Year 1 annual platform-only cost, min $3,000)
  const platformAnnual = tier.monthly * 12 * (1 - contractDiscountPct);
  const setupFee = Math.max(SETUP_FEE_MIN, platformAnnual * SETUP_FEE_PCT);

  const year1Total = annualAfterDiscount + setupFee;

  // IVR tier minimum: Enterprise IVR ($3,700) requires the Enterprise base plan.
  // Lower tiers (Essentials/Pro/Business) require Professional+ as noted in tier metadata.
  const ivrTierPrice = channels.ivr ? IVR_TIERS[addons.ivrTier].price : 0;
  const ivrFloorWarning = channels.ivr && addons.ivrTier === "enterprise" && tier.monthly < 1497;
  const ivrFloorShortfall = ivrFloorWarning ? Math.max(0, 1497 - tier.monthly) : 0;
  void ivrTierPrice;

  return {
    lines,
    monthlySubtotal,
    monthlyAfterDiscount,
    annualAfterDiscount,
    setupFee,
    year1Total,
    discountPct: contractDiscountPct * 100,
    contractDiscountPct,
    ivrFloorWarning,
    ivrFloorShortfall,
    tierName: tier.name,
    tierConversations: tier.conversations,
  };
}

// ── Formatting ────────────────────────────────────────────────────────────

const fmt = (n: number) =>
  "$" + n.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 });

const STEPS = [
  "Customer Info",
  "Volume Discovery",
  "Channels",
  "Journey Complexity",
  "Commerce",
  "Knowledge & AI Index",
  "Add-ons",
  "Contract Term",
];

const VERTICALS: Vertical[] = [
  "Casino/Gaming",
  "E-commerce",
  "Healthcare",
  "Manufacturing",
  "Staffing / HR Tech",
  "SaaS",
  "Financial Services",
  "Hospitality",
  "Government",
  "Other",
];

// ── Default state ─────────────────────────────────────────────────────────

const DEFAULT_STATE: WizardState = {
  step: 0,
  customer: { companyName: "", contactName: "", contactEmail: "", vertical: "" },
  volume: { monthlyConversations: 1000, monthlyCallsIfIvr: 0, monthlyBridgeMinutes: 0, monthlyOutboundMinutes: 0, monthlyWebVisitors: 0, monthlyEmails: 0, peakMonthMultiplier: 1.5 },
  channels: { webChat: true, email: false, sms: false, whatsapp: false, ivr: false },
  journey: { level: "L1", integrationCount: 0 },
  commerce: {
    platform: "none", corePackage: false, commercePackage: false,
    b2bOpsPackage: false, logisticsPackage: false,
    agentSeats: 0, botConversationsPerMonth: 0, skuCount: 0,
    storefrontCount: 1, pilotMonths: 0, existingContractBase: 0, perAgentRate: 0,
  },
  addons: {
    languagePack: 0, hipaa: false, pci: false, dataResidency: false,
    ivrTier: "enterprise" as IvrTier,
    smsBundle: false, whatsappBundle: false, standardApi: false,
    preBuiltIntegrationCount: 0, complexIntegrationCount: 0,
    aiEmailAddOn: false,
    recruitingBot: false, recruitingBotAtsIntegration: false,
    recruitingBotGlobalHiring: false, recruitingBotEeoc: false,
    recruitingBotCalendarScreening: false,
  },
  knowledge: {
    kbArticleCount: 0,
    scraperPageCount: 0,
    pdfPageCount: 0,
    hasPdfUploads: false,
    hasOneDriveIntegration: false,
  },
  contractTerm: "annual",
  l3Orchestration: false,
  extraConversationBlocks: 0,
  extraWorkflowAiBlocks: 0,
  extraMcpBlocks: 0,
  extraSmsBlocks: 0,
  extraIvrBlocks: 0,
};

// ── Styles helpers ────────────────────────────────────────────────────────

const card = "bg-slate-800 border border-slate-700 rounded-xl p-5";
const label = "block text-slate-300 text-sm font-medium mb-1";
const input = "w-full bg-slate-900 border border-slate-600 text-white rounded px-3 py-2 text-sm focus:outline-none focus:border-blue-500";
const select = "w-full bg-slate-900 border border-slate-600 text-white rounded px-3 py-2 text-sm focus:outline-none focus:border-blue-500";
const btnPrimary = "px-4 py-2 bg-[var(--color-primary)] hover:bg-blue-500 text-white rounded-lg text-sm font-medium transition-colors";
const btnSecondary = "px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg text-sm font-medium transition-colors";

// ── Main component ────────────────────────────────────────────────────────

export default function PricingQuote() {
  // Live pricing from DB — overrides the static fallback constants above.
  // Prices update on next page load after a superadmin changes them via PUT /Pricing/catalog/{key}.
  const { catalog } = usePricingCatalog();

  // Derive live tier prices from the catalog (fallback to static constants if catalog not yet loaded)
  const liveTiers = useMemo(() => {
    const s = catalog.byKey["plan.starter"];
    const p = catalog.byKey["plan.professional"];
    const e = catalog.byKey["plan.enterprise"];
    const meta = (pkg: typeof s) => { try { return pkg?.metadataJson ? JSON.parse(pkg.metadataJson) : {}; } catch { return {}; } };
    return [
      { name: "Starter",      monthly: s ? s.monthlyPriceCents / 100 : 497,   conversations: meta(s).includedConversations ?? 1000,  label: `Starter — $${s ? (s.monthlyPriceCents/100).toLocaleString() : 497}/mo` },
      { name: "Professional", monthly: p ? p.monthlyPriceCents / 100 : 1497,  conversations: meta(p).includedConversations ?? 5000,  label: `Professional — $${p ? (p.monthlyPriceCents/100).toLocaleString() : 1497}/mo` },
      { name: "Enterprise",   monthly: e ? e.monthlyPriceCents / 100 : 7997,  conversations: meta(e).includedConversations ?? 25000, label: "Enterprise — custom pricing" },
    ];
  }, [catalog]);

  // Live IVR tier prices from catalog (available for future live-price wiring in calculateQuote)
  const liveIvrTierPrices: Record<IvrTier, number> = {
    essentials:   catalog.price("addon.ivr.essentials")   || IVR_TIERS.essentials.price,
    professional: catalog.price("addon.ivr.professional") || IVR_TIERS.professional.price,
    business:     catalog.price("addon.ivr.business")     || IVR_TIERS.business.price,
    enterprise:   catalog.price("addon.ivr")              || IVR_TIERS.enterprise.price,
  };
  void liveIvrTierPrices;
  const liveConvBlockPrice     = catalog.price("block.conversation")         || CONVERSATION_BLOCK_PRICE;
  const liveConvBlockSize      = catalog.byKey["block.conversation"]?.blockSize ?? CONVERSATION_BLOCK_SIZE;
  const liveHipaaPrice         = catalog.price("compliance.hipaa")     || 2000;
  const livePciPrice           = catalog.price("compliance.pci")       || 1500;
  const liveDataResidencyPrice = catalog.price("compliance.data-residency") || 500;
  const liveAiEmailPrice       = catalog.price("addon.ai-email")       || AI_EMAIL_ADDON_PRICE;
  const liveL3Price            = catalog.price("addon.l3-orchestration") || 1500;
  const liveWhatsappPrice      = catalog.price("addon.whatsapp")       || 500;
  const liveSmsPrice           = catalog.price("addon.sms-bundle")     || 500;
  const liveLangPrices: Record<number, number> = {
    0: 0,
    1: catalog.price("addon.lang-1") || 300,
    3: catalog.price("addon.lang-3") || 600,
    5: catalog.price("addon.lang-5") || 900,
  };
  const liveIvrMultilingualPrices: Record<number, number> = {
    0: 0,
    3: catalog.price("addon.ivr.multilingual-3") || 600,
    5: catalog.price("addon.ivr.multilingual-5") || 900,
  };
  const liveCommercePackages = {
    core:      { ...COMMERCE_PACKAGES.core,      retail: (catalog.byKey["skill.commerce-core"]?.retailPriceCents     ?? 50000)  / 100, marked: catalog.price("skill.commerce-core")      || 250 },
    commerce:  { ...COMMERCE_PACKAGES.commerce,  retail: (catalog.byKey["skill.commerce-commerce"]?.retailPriceCents ?? 100000) / 100, marked: catalog.price("skill.commerce-commerce")  || 500 },
    b2bOps:    { ...COMMERCE_PACKAGES.b2bOps,    retail: (catalog.byKey["skill.commerce-b2bops"]?.retailPriceCents   ?? 100000) / 100, marked: catalog.price("skill.commerce-b2bops")    || 500 },
    logistics: { ...COMMERCE_PACKAGES.logistics, retail: (catalog.byKey["skill.commerce-logistics"]?.retailPriceCents ?? 50000) / 100, marked: catalog.price("skill.commerce-logistics") || 250 },
  };
  const liveCommerceAllInRetail = (catalog.byKey["skill.commerce-allin"]?.retailPriceCents ?? 250000) / 100;
  const liveCommerceAllInMarked = catalog.price("skill.commerce-allin") || 1200;

  const [state, setState] = useState<WizardState>(DEFAULT_STATE);
  const [showProposal, setShowProposal] = useState(false);
  const [proposalCopied, setProposalCopied] = useState(false);
  const [flagNotes, setFlagNotes] = useState<FlagNote[]>(() => {
    try {
      const raw = localStorage.getItem("pricingQuote_flagNotes");
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  });
  const [newNote, setNewNote] = useState("");

  const quote = calculateQuote(state);

  const update = useCallback((patch: Partial<WizardState>) => {
    setState(prev => ({ ...prev, ...patch }));
  }, []);

  const updateCustomer = (patch: Partial<CustomerInfo>) =>
    update({ customer: { ...state.customer, ...patch } });

  const updateVolume = (patch: Partial<VolumeDiscovery>) =>
    update({ volume: { ...state.volume, ...patch } });

  const updateChannels = (patch: Partial<Channels>) =>
    update({ channels: { ...state.channels, ...patch } });

  const updateJourney = (patch: Partial<WizardState["journey"]>) =>
    update({ journey: { ...state.journey, ...patch } });

  const updateCommerce = (patch: Partial<CommerceConfig>) =>
    update({ commerce: { ...state.commerce, ...patch } });

  const updateAddons = (patch: Partial<AddOns>) =>
    update({ addons: { ...state.addons, ...patch } });

  const updateKnowledge = (patch: Partial<KnowledgeConfig>) =>
    update({ knowledge: { ...state.knowledge, ...patch } });

  // Auto-preselect knowledge defaults when vertical or commerce platform changes.
  // Dependency array already guarantees this only fires on change; guard skips initial empty vertical.
  useEffect(() => {
    if (state.customer.vertical) {
      update({ knowledge: getKnowledgeDefaults(state.customer.vertical, state.commerce.platform) });
    }
  }, [state.customer.vertical, state.commerce.platform]);

  const addFlagNote = () => {
    if (!newNote.trim()) return;
    const updated = [...flagNotes, { note: newNote.trim(), savedAt: new Date().toLocaleString() }];
    setFlagNotes(updated);
    localStorage.setItem("pricingQuote_flagNotes", JSON.stringify(updated));
    setNewNote("");
  };

  const removeFlagNote = (i: number) => {
    const updated = flagNotes.filter((_, idx) => idx !== i);
    setFlagNotes(updated);
    localStorage.setItem("pricingQuote_flagNotes", JSON.stringify(updated));
  };

  const proposalText = generateProposalText(state, quote);

  const copyProposal = () => {
    navigator.clipboard.writeText(proposalText).then(() => {
      setProposalCopied(true);
      setTimeout(() => setProposalCopied(false), 2000);
    });
  };

  const printProposal = () => window.print();

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white">Pricing &amp; Proposal Builder</h1>
        <p className="text-slate-400 text-sm mt-1">
          Build a custom quote — IVR, AI, commerce integrations, skill packages. All calculations are local.
        </p>
        <p className="text-amber-400 text-xs mt-1 font-medium">
          Velaro internal use only — pricing data is hardcoded in this tool and reflects internal rate cards. Do not share this page URL with customers.
        </p>
      </div>

      <div className="flex flex-col xl:flex-row gap-6 items-start">
        {/* Left: wizard */}
        <div className="flex-1 min-w-0 space-y-5">
          {/* Step progress */}
          <StepProgress current={state.step} total={STEPS.length} labels={STEPS} />

          {/* Step content */}
          {state.step === 0 && (
            <StepCustomerInfo customer={state.customer} onChange={updateCustomer} />
          )}
          {state.step === 1 && (
            <StepVolumeDiscovery volume={state.volume} channels={state.channels} onChange={updateVolume} />
          )}
          {state.step === 2 && (
            <StepChannels channels={state.channels} onChange={updateChannels} />
          )}
          {state.step === 3 && (
            <StepJourney journey={state.journey} onChange={updateJourney} />
          )}
          {state.step === 4 && (
            <StepCommerce commerce={state.commerce} onChange={updateCommerce} />
          )}
          {state.step === 5 && (
            <StepKnowledge
              knowledge={state.knowledge}
              vertical={state.customer.vertical}
              platform={state.commerce.platform}
              onChange={updateKnowledge}
            />
          )}
          {state.step === 6 && (
            <StepAddons
              addons={state.addons}
              channels={state.channels}
              l3Orchestration={state.l3Orchestration}
              extraBlocks={state.extraConversationBlocks}
              extraWorkflowAiBlocks={state.extraWorkflowAiBlocks}
              extraMcpBlocks={state.extraMcpBlocks}
              extraSmsBlocks={state.extraSmsBlocks}
              extraIvrBlocks={state.extraIvrBlocks}
              onChange={updateAddons}
              onChangeL3={(v) => update({ l3Orchestration: v })}
              onChangeBlocks={(v) => update({ extraConversationBlocks: v })}
              onChangeWorkflowAi={(v) => update({ extraWorkflowAiBlocks: v })}
              onChangeMcp={(v) => update({ extraMcpBlocks: v })}
              onChangeSms={(v) => update({ extraSmsBlocks: v })}
              onChangeIvr={(v) => update({ extraIvrBlocks: v })}
            />
          )}
          {state.step === 7 && (
            <StepContractTerm term={state.contractTerm} onChange={(t) => update({ contractTerm: t })} />
          )}

          {/* Navigation */}
          <div className="flex justify-between">
            <button
              className={btnSecondary}
              onClick={() => update({ step: Math.max(0, state.step - 1) })}
              disabled={state.step === 0}
            >
              Back
            </button>
            {state.step < STEPS.length - 1 ? (
              <button
                className={btnPrimary}
                onClick={() => update({ step: state.step + 1 })}
              >
                Next &rarr;
              </button>
            ) : (
              <button
                className="px-4 py-2 bg-green-600 hover:bg-green-500 text-white rounded-lg text-sm font-medium transition-colors"
                onClick={() => setShowProposal(true)}
              >
                Generate Proposal
              </button>
            )}
          </div>

          {/* Flag for AM */}
          <FlagForAM notes={flagNotes} newNote={newNote} onNewNote={setNewNote} onAdd={addFlagNote} onRemove={removeFlagNote} />

          {/* IVR Knowledge Reference */}
          {state.channels.ivr && <IvrKnowledgePanel />}
          {/* Commerce Knowledge Reference */}
          {state.commerce.platform !== "none" && <CommerceKnowledgePanel platform={state.commerce.platform} />}
        </div>

        {/* Right: live quote panel */}
        <div className="xl:w-96 w-full xl:sticky xl:top-6 space-y-4">
          <QuotePanel quote={quote} channels={state.channels} tier={selectTier(state.volume.monthlyConversations)} contractTerm={state.contractTerm} customer={state.customer} />
        </div>
      </div>

      {/* Proposal modal */}
      {showProposal && (
        <ProposalModal
          text={proposalText}
          copied={proposalCopied}
          onCopy={copyProposal}
          onPrint={printProposal}
          onClose={() => setShowProposal(false)}
          state={state}
        />
      )}
    </div>
  );
}

// ── Step progress ─────────────────────────────────────────────────────────

function StepProgress({ current, total, labels }: { current: number; total: number; labels: string[] }) {
  return (
    <div className="overflow-x-auto">
      <div className="flex items-center gap-0 min-w-max">
        {labels.map((lbl, i) => (
          <React.Fragment key={i}>
            <div className="flex flex-col items-center">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold border-2 transition-colors ${
                i < current ? "bg-blue-600 border-blue-600 text-white" :
                i === current ? "bg-slate-800 border-blue-400 text-blue-400" :
                "bg-slate-800 border-slate-600 text-slate-500"
              }`}>
                {i < current ? "✓" : i + 1}
              </div>
              <div className={`text-xs mt-1 text-center max-w-16 leading-tight ${i === current ? "text-blue-300" : i < current ? "text-slate-400" : "text-slate-600"}`}>
                {lbl}
              </div>
            </div>
            {i < total - 1 && (
              <div className={`h-0.5 w-8 mb-5 mx-1 transition-colors ${i < current ? "bg-blue-600" : "bg-slate-700"}`} />
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

// ── Step 1: Customer Info ─────────────────────────────────────────────────

function StepCustomerInfo({ customer, onChange }: { customer: CustomerInfo; onChange: (p: Partial<CustomerInfo>) => void }) {
  return (
    <div className={card}>
      <h2 className="text-white font-semibold mb-4">Step 1 — Customer Info</h2>
      <div className="space-y-4">
        <div>
          <label className={label}>Company Name</label>
          <input className={input} value={customer.companyName} onChange={e => onChange({ companyName: e.target.value })} placeholder="Acme Corp" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={label}>Contact Name</label>
            <input className={input} value={customer.contactName} onChange={e => onChange({ contactName: e.target.value })} placeholder="Jane Smith" />
          </div>
          <div>
            <label className={label}>Contact Email</label>
            <input className={input} type="email" value={customer.contactEmail} onChange={e => onChange({ contactEmail: e.target.value })} placeholder="jane@acme.com" />
          </div>
        </div>
        <div>
          <label className={label}>Industry / Vertical</label>
          <select className={select} value={customer.vertical} onChange={e => onChange({ vertical: e.target.value as Vertical })}>
            <option value="">Select vertical...</option>
            {VERTICALS.map(v => <option key={v} value={v}>{v}</option>)}
          </select>
        </div>
      </div>
    </div>
  );
}

// ── Step 2: Volume Discovery ───────────────────────────────────────────────

function StepVolumeDiscovery({ volume, channels, onChange }: { volume: VolumeDiscovery; channels: Channels; onChange: (p: Partial<VolumeDiscovery>) => void }) {
  const tier = selectTier(volume.monthlyConversations);
  return (
    <div className={card}>
      <h2 className="text-white font-semibold mb-1">Step 2 — Volume Discovery</h2>
      <p className="text-slate-400 text-xs mb-4">Numbers drive automatic tier selection and IVR carrier block sizing.</p>
      <div className="space-y-4">
        <div>
          <label className={label}>Monthly Conversations / Chats</label>
          <input className={input} type="number" min={0} value={volume.monthlyConversations}
            onChange={e => onChange({ monthlyConversations: Math.max(0, Number(e.target.value)) })} />
          <div className="mt-1 text-xs text-blue-400">Auto-selected tier: <span className="font-semibold">{tier.name}</span> ({tier.conversations.toLocaleString()} included)</div>
        </div>

        {channels.ivr && (
          <>
            <div>
              <label className={label}>Monthly IVR AI Inbound Minutes</label>
              <input className={input} type="number" min={0} value={volume.monthlyCallsIfIvr}
                onChange={e => onChange({ monthlyCallsIfIvr: Math.max(0, Number(e.target.value)) })} />
              <div className="mt-1 text-xs text-slate-400">Time caller spends with the AI bot. Essentials: 1K included · Pro: 3K · Business: 7.5K · Enterprise: 10K</div>
            </div>
            <div>
              <label className={label}>Monthly Bridge/Transfer Minutes</label>
              <input className={input} type="number" min={0} value={volume.monthlyBridgeMinutes}
                onChange={e => onChange({ monthlyBridgeMinutes: Math.max(0, Number(e.target.value)) })} placeholder="0" />
              <div className="mt-1 text-xs text-slate-400">Minutes on hold or talking to a live agent after AI transfers. Pro: 100 included · Business: 500 · Enterprise: 1K. Overage $200/1K min.</div>
            </div>
            <div>
              <label className={label}>Monthly Outbound Campaign Minutes <span className="text-amber-400">(Enterprise only)</span></label>
              <input className={input} type="number" min={0} value={volume.monthlyOutboundMinutes}
                onChange={e => onChange({ monthlyOutboundMinutes: Math.max(0, Number(e.target.value)) })} placeholder="0" />
              <div className="mt-1 text-xs text-slate-400">AI-initiated outbound calls (reminders, campaigns, callbacks). $500/2,500 min block.</div>
            </div>
          </>
        )}

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={label}>Monthly Website Visitors</label>
            <input className={input} type="number" min={0} value={volume.monthlyWebVisitors}
              onChange={e => onChange({ monthlyWebVisitors: Math.max(0, Number(e.target.value)) })} placeholder="Optional" />
          </div>
          <div>
            <label className={label}>Monthly Support Emails</label>
            <input className={input} type="number" min={0} value={volume.monthlyEmails}
              onChange={e => onChange({ monthlyEmails: Math.max(0, Number(e.target.value)) })} placeholder="Optional" />
          </div>
        </div>

        <div>
          <label className={label}>Peak Month Multiplier</label>
          <select className={select} value={volume.peakMonthMultiplier}
            onChange={e => onChange({ peakMonthMultiplier: Number(e.target.value) })}>
            <option value={1}>1× — flat all year</option>
            <option value={1.25}>1.25× — slight seasonality</option>
            <option value={1.5}>1.5× — moderate peak (default)</option>
            <option value={2}>2× — strong seasonality (e.g. retail)</option>
            <option value={3}>3× — extreme peak (e.g. casino events)</option>
          </select>
          {volume.peakMonthMultiplier > 1 && (
            <div className="mt-1 text-xs text-amber-400">
              Peak month estimate: ~{Math.round(volume.monthlyConversations * volume.peakMonthMultiplier).toLocaleString()} conversations
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Step 3: Channels ──────────────────────────────────────────────────────

function StepChannels({ channels, onChange }: { channels: Channels; onChange: (p: Partial<Channels>) => void }) {
  const channelDefs: { key: keyof Channels; label: string; desc: string; badge?: string }[] = [
    { key: "webChat", label: "Web Chat", desc: "Live chat widget on your website" },
    { key: "email",   label: "Email",    desc: "Ticket-style email support" },
    { key: "sms",     label: "SMS",      desc: "SMS / text messaging (+$500/mo)", badge: "+$500" },
    { key: "whatsapp", label: "WhatsApp", desc: "WhatsApp Business API (+$500/mo)", badge: "+$500" },
    { key: "ivr",     label: "Voice / IVR", desc: "AI phone IVR — ElevenLabs voice, live transfer, screen pop, outbound campaigns. From $499/mo.", badge: "+$499+" },
  ];

  return (
    <div className={card}>
      <h2 className="text-white font-semibold mb-1">Step 3 — Channels</h2>
      <p className="text-slate-400 text-xs mb-4">Select all channels this customer needs. IVR triggers carrier cost fields.</p>
      <div className="space-y-3">
        {channelDefs.map(c => (
          <label key={c.key} className="flex items-center gap-3 cursor-pointer group">
            <input
              type="checkbox"
              checked={channels[c.key]}
              onChange={e => onChange({ [c.key]: e.target.checked } as Partial<Channels>)}
              className="w-4 h-4 accent-blue-500"
            />
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <span className="text-white text-sm font-medium group-hover:text-blue-300 transition-colors">{c.label}</span>
                {c.badge && <span className="text-xs bg-amber-900/50 text-amber-400 border border-amber-700 rounded px-1.5 py-0.5">{c.badge}</span>}
              </div>
              <div className="text-slate-400 text-xs">{c.desc}</div>
            </div>
          </label>
        ))}
      </div>
    </div>
  );
}

// ── Step 4: Journey Complexity ─────────────────────────────────────────────

function StepJourney({ journey, onChange }: { journey: WizardState["journey"]; onChange: (p: Partial<WizardState["journey"]>) => void }) {
  const levels: { level: JourneyLevel; title: string; desc: string; examples: string }[] = [
    {
      level: "L1",
      title: "L1 — Simple FAQ / Triage",
      desc: "Bot answers FAQs, collects info, routes to agent. No external system calls.",
      examples: "E.g.: Hours of operation, store location, basic product info, lead capture.",
    },
    {
      level: "L2",
      title: "L2 — Transactional",
      desc: "Bot reads from or writes to 1–3 external systems (CRM, OMS, scheduling).",
      examples: "E.g.: Order status lookup, appointment booking, account balance, returns.",
    },
    {
      level: "L3",
      title: "L3 — Complex Orchestration",
      desc: "Multi-agent coordination, 4+ integrations, advanced branching, AI-to-AI delegation.",
      examples: "E.g.: Full commerce automation, multi-system updates, compliance workflows.",
    },
  ];

  return (
    <div className={card}>
      <h2 className="text-white font-semibold mb-1">Step 4 — Journey Complexity</h2>
      <p className="text-slate-400 text-xs mb-4">This determines whether the L3 Orchestration add-on is needed (+$1,500/mo).</p>
      <div className="space-y-3">
        {levels.map(l => (
          <label key={l.level} className={`flex items-start gap-3 cursor-pointer rounded-lg border p-3 transition-colors ${journey.level === l.level ? "border-blue-500 bg-blue-950/30" : "border-slate-700 hover:border-slate-500"}`}>
            <input
              type="radio"
              name="journeyLevel"
              value={l.level}
              checked={journey.level === l.level}
              onChange={() => onChange({ level: l.level })}
              className="mt-1 accent-blue-500"
            />
            <div>
              <div className="text-white text-sm font-medium">{l.title}</div>
              <div className="text-slate-400 text-xs mt-0.5">{l.desc}</div>
              <div className="text-slate-500 text-xs mt-0.5 italic">{l.examples}</div>
            </div>
          </label>
        ))}
      </div>

      {(journey.level === "L2" || journey.level === "L3") && (
        <div className="mt-4">
          <label className={label}>Number of integrations</label>
          <input
            className={input}
            type="number"
            min={0}
            max={20}
            value={journey.integrationCount}
            onChange={e => onChange({ integrationCount: Math.max(0, Number(e.target.value)) })}
          />
        </div>
      )}

      {journey.level === "L3" && (
        <div className="mt-3 bg-amber-900/20 border border-amber-700 rounded p-3 text-xs text-amber-300">
          L3 automatically includes the Orchestration Upgrade (+$1,500/mo). This will appear in your quote.
        </div>
      )}
    </div>
  );
}

// ── Step 5: Commerce Integration ──────────────────────────────────────────

function StepCommerce({ commerce, onChange }: { commerce: CommerceConfig; onChange: (p: Partial<CommerceConfig>) => void }) {
  const platforms: CommercePlatform[] = ["none", "bigcommerce", "shopify", "woocommerce", "magento", "square"];
  const totalSkills = (commerce.corePackage ? COMMERCE_PACKAGES.core.skills : 0)
    + (commerce.commercePackage ? COMMERCE_PACKAGES.commerce.skills : 0)
    + (commerce.b2bOpsPackage ? COMMERCE_PACKAGES.b2bOps.skills : 0)
    + (commerce.logisticsPackage ? COMMERCE_PACKAGES.logistics.skills : 0);

  const allSelected = commerce.corePackage && commerce.commercePackage && commerce.b2bOpsPackage && commerce.logisticsPackage;
  const packageMonthly = (commerce.corePackage ? COMMERCE_PACKAGES.core.marked : 0)
    + (commerce.commercePackage ? COMMERCE_PACKAGES.commerce.marked : 0)
    + (commerce.b2bOpsPackage ? COMMERCE_PACKAGES.b2bOps.marked : 0)
    + (commerce.logisticsPackage ? COMMERCE_PACKAGES.logistics.marked : 0);
  const allInSavings = allSelected ? packageMonthly - COMMERCE_ALL_IN_MARKED : 0;

  return (
    <div className={card}>
      <h2 className="text-white font-semibold mb-1">Step 5 — Commerce Integration</h2>
      <p className="text-slate-400 text-xs mb-4">
        Select a commerce platform and skill packages. Skill packages are add-ons on top of the platform tier.
        {commerce.platform !== "none" && (
          <span className="ml-1 text-blue-400">
            {COMMERCE_PLATFORM_LABELS[commerce.platform]} has {COMMERCE_PLATFORM_SKILLS[commerce.platform]} total skills available.
          </span>
        )}
      </p>

      <div className="space-y-5">
        {/* Platform */}
        <div>
          <label className={label}>Commerce Platform</label>
          <select className={select} value={commerce.platform}
            onChange={e => {
              const p = e.target.value as CommercePlatform;
              onChange({
                platform: p,
                corePackage: p !== "none",
                commercePackage: false,
                b2bOpsPackage: false,
                logisticsPackage: false,
              });
            }}>
            {platforms.map(p => (
              <option key={p} value={p}>
                {COMMERCE_PLATFORM_LABELS[p]}
                {p !== "none" ? ` (${COMMERCE_PLATFORM_SKILLS[p]} skills)` : ""}
              </option>
            ))}
          </select>
        </div>

        {commerce.platform !== "none" && (
          <>
            {/* Existing contract (for upsell deals) */}
            <div className="bg-slate-900 border border-slate-600 rounded-lg p-4 space-y-3">
              <div className="text-slate-300 text-sm font-medium">Existing Contract (for upsells)</div>
              <p className="text-slate-500 text-xs">If this customer already has a Velaro contract, enter their current base. Leave 0 for new customers.</p>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="text-slate-400 text-xs block mb-1">Monthly base ($)</label>
                  <input className={input} type="number" min={0} value={commerce.existingContractBase}
                    onChange={e => onChange({ existingContractBase: Math.max(0, Number(e.target.value)) })} />
                </div>
                <div>
                  <label className="text-slate-400 text-xs block mb-1">Agent seats</label>
                  <input className={input} type="number" min={0} value={commerce.agentSeats}
                    onChange={e => onChange({ agentSeats: Math.max(0, Number(e.target.value)) })} />
                </div>
                <div>
                  <label className="text-slate-400 text-xs block mb-1">Per-agent rate ($)</label>
                  <input className={input} type="number" min={0} step={0.01} value={commerce.perAgentRate}
                    onChange={e => onChange({ perAgentRate: Math.max(0, Number(e.target.value)) })} />
                </div>
              </div>
            </div>

            {/* Skill Packages */}
            <div>
              <div className={label}>AI Skill Packages</div>
              <div className="space-y-3">
                {(Object.entries(COMMERCE_PACKAGES) as [string, typeof COMMERCE_PACKAGES.core][]).map(([key, pkg]) => {
                  const isChecked = commerce[`${key}Package` as keyof CommerceConfig] as boolean;
                  const isCore = key === "core";
                  return (
                    <label key={key} className={`flex items-start gap-3 cursor-pointer rounded-lg border p-3 transition-colors ${isChecked ? "border-blue-500 bg-blue-950/30" : "border-slate-700 hover:border-slate-500"}`}>
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={e => onChange({ [`${key}Package`]: e.target.checked } as Partial<CommerceConfig>)}
                        className="mt-1 w-4 h-4 accent-blue-500"
                        disabled={isCore}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-white text-sm font-medium">{pkg.name} Package</span>
                          <span className="text-xs bg-slate-700 text-slate-300 rounded px-1.5 py-0.5">{pkg.skills} skills</span>
                          {isCore && <span className="text-xs bg-green-900/50 text-green-400 border border-green-700 rounded px-1.5 py-0.5">Included</span>}
                        </div>
                        <div className="text-slate-400 text-xs mt-0.5">{pkg.desc}</div>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-slate-500 text-xs line-through">{fmt(pkg.retail)}/mo</span>
                          <span className="text-green-400 text-xs font-semibold">{fmt(pkg.marked)}/mo</span>
                        </div>
                      </div>
                    </label>
                  );
                })}
              </div>

              {/* All-in bundle hint */}
              {!allSelected && totalSkills > 0 && (
                <div className="mt-2 bg-blue-900/20 border border-blue-700 rounded p-2 text-xs text-blue-300">
                  Select all 4 packages for the All-In Bundle at {fmt(COMMERCE_ALL_IN_MARKED)}/mo (saves {fmt(COMMERCE_ALL_IN_RETAIL - COMMERCE_ALL_IN_MARKED)}/mo vs retail).
                </div>
              )}
              {allSelected && allInSavings > 0 && (
                <div className="mt-2 bg-green-900/20 border border-green-700 rounded p-2 text-xs text-green-300">
                  All-In Bundle active — saving {fmt(allInSavings)}/mo vs. individual package pricing.
                </div>
              )}

              {totalSkills > 0 && (
                <div className="mt-2 text-xs text-slate-400">
                  Selected: <span className="text-white font-semibold">{totalSkills} skills</span> of {COMMERCE_PLATFORM_SKILLS[commerce.platform]} available
                </div>
              )}
            </div>

            {/* Volume sizing */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={label}>Bot Conversations / Month</label>
                <input className={input} type="number" min={0} step={500} value={commerce.botConversationsPerMonth}
                  onChange={e => onChange({ botConversationsPerMonth: Math.max(0, Number(e.target.value)) })} />
                {commerce.botConversationsPerMonth > 0 && (
                  <div className="text-xs text-blue-400 mt-1">
                    {Math.ceil(commerce.botConversationsPerMonth / BOT_CONVERSATION_BLOCK_SIZE)} blocks × {fmt(BOT_CONVERSATION_BLOCK_PRICE)} = {fmt(Math.ceil(commerce.botConversationsPerMonth / BOT_CONVERSATION_BLOCK_SIZE) * BOT_CONVERSATION_BLOCK_PRICE)}/mo
                  </div>
                )}
              </div>
              <div>
                <label className={label}>SKU Count (products to index)</label>
                <input className={input} type="number" min={0} step={1000} value={commerce.skuCount}
                  onChange={e => onChange({ skuCount: Math.max(0, Number(e.target.value)) })} />
                {commerce.skuCount > 10000 && (
                  <div className="text-xs text-amber-400 mt-1">
                    10K included, +{fmt(Math.ceil((commerce.skuCount - 10000) / 10000) * 50)}/mo for {(commerce.skuCount - 10000).toLocaleString()} extra
                  </div>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={label}>Storefronts</label>
                <input className={input} type="number" min={1} max={10} value={commerce.storefrontCount}
                  onChange={e => onChange({ storefrontCount: Math.max(1, Number(e.target.value)) })} />
                {commerce.storefrontCount > 1 && (
                  <div className="text-xs text-slate-400 mt-1">1 included, +{fmt(100)}/mo per additional</div>
                )}
              </div>
              <div>
                <label className={label}>Pilot Period (free months)</label>
                <select className={select} value={commerce.pilotMonths}
                  onChange={e => onChange({ pilotMonths: Number(e.target.value) })}>
                  <option value={0}>No pilot — charge from day 1</option>
                  <option value={1}>1 month free</option>
                  <option value={2}>2 months free</option>
                  <option value={3}>3 months free (recommended)</option>
                  <option value={6}>6 months free (strategic)</option>
                </select>
                {commerce.pilotMonths > 0 && (
                  <div className="text-xs text-amber-400 mt-1">
                    Commerce add-ons waived for {commerce.pilotMonths} months. Base contract unchanged.
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── Step 6: Knowledge & AI Index ─────────────────────────────────────────

function StepKnowledge({
  knowledge, vertical, platform, onChange,
}: {
  knowledge: KnowledgeConfig;
  vertical: Vertical | "";
  platform: CommercePlatform;
  onChange: (p: Partial<KnowledgeConfig>) => void;
}) {
  const kbTier = getKnowledgeTier(knowledge.kbArticleCount, KB_ARTICLE_PRO_THRESHOLD, KB_ARTICLE_ENT_THRESHOLD);
  const crawlTier = getKnowledgeTier(knowledge.scraperPageCount, SCRAPER_PAGE_PRO_THRESHOLD, SCRAPER_PAGE_ENT_THRESHOLD);
  const overallTier: KnowledgeTier = kbTier === "Enterprise" || crawlTier === "Enterprise" ? "Enterprise"
    : kbTier === "Pro" || crawlTier === "Pro" ? "Pro" : "Basic";

  return (
    <div className={card}>
      <h2 className="text-white font-semibold mb-1">Step 6 — Knowledge &amp; AI Index</h2>
      <p className="text-slate-400 text-xs mb-4">
        Velaro AI searches your content to answer customer questions in real time.
        Tell us how much content you have so we can right-size the knowledge tier.
        {vertical && <span className="ml-1 text-blue-400">Pre-filled for {vertical}.</span>}
      </p>

      {/* Tier match indicator */}
      <div className={`mb-4 p-3 rounded-lg border ${KNOWLEDGE_TIER_BORDERS[overallTier]}`}>
        <div className="text-xs font-semibold text-slate-300 mb-0.5">Knowledge Tier Match</div>
        <div className={`text-sm font-bold ${KNOWLEDGE_TIER_COLORS[overallTier]}`}>{overallTier}</div>
        {overallTier !== "Basic" && (
          <div className="text-xs text-slate-400 mt-0.5">Knowledge add-on pricing applies — see quote panel on the right.</div>
        )}
        {overallTier === "Basic" && (
          <div className="text-xs text-slate-400 mt-0.5">All knowledge needs included in base plan.</div>
        )}
      </div>

      <div className="space-y-5">
        {/* KB Articles */}
        <div>
          <label className={label}>Knowledge Base Articles</label>
          <input
            className={input}
            type="number"
            min={0}
            step={50}
            value={knowledge.kbArticleCount}
            onChange={e => onChange({ kbArticleCount: Math.max(0, Number(e.target.value)) })}
          />
          <div className="mt-1 text-xs text-slate-400">
            How many help articles, FAQs, or policy docs will the bot reference?
            {" "}<span className={`font-medium ${KNOWLEDGE_TIER_COLORS[kbTier]}`}>
              {kbTier === "Basic" ? `≤${KB_ARTICLE_PRO_THRESHOLD} — included in all plans`
                : kbTier === "Pro" ? `${KB_ARTICLE_PRO_THRESHOLD + 1}–${KB_ARTICLE_ENT_THRESHOLD.toLocaleString()} — +$${KB_ARTICLE_PRO_PRICE}/mo`
                : `${KB_ARTICLE_ENT_THRESHOLD.toLocaleString()}+ — +$${KB_ARTICLE_PRO_PRICE} Pro +$${KB_ARTICLE_ENT_PRICE} Ent = $${KB_ARTICLE_PRO_PRICE + KB_ARTICLE_ENT_PRICE}/mo total`}
            </span>
          </div>
        </div>

        {/* Scraper Pages */}
        <div>
          <label className={label}>Web Pages to Index / Month</label>
          <input
            className={input}
            type="number"
            min={0}
            step={500}
            value={knowledge.scraperPageCount}
            onChange={e => onChange({ scraperPageCount: Math.max(0, Number(e.target.value)) })}
          />
          <div className="mt-1 text-xs text-slate-400">
            Product pages, blog posts, docs, pricing — anything the bot should be able to reference.
            {" "}<span className={`font-medium ${KNOWLEDGE_TIER_COLORS[crawlTier]}`}>
              {crawlTier === "Basic" ? `≤${SCRAPER_PAGE_PRO_THRESHOLD} pages/mo — included`
                : crawlTier === "Pro" ? `${SCRAPER_PAGE_PRO_THRESHOLD + 1}–${SCRAPER_PAGE_ENT_THRESHOLD.toLocaleString()} pages/mo — +$${SCRAPER_PAGE_PRO_PRICE}/mo`
                : `${SCRAPER_PAGE_ENT_THRESHOLD.toLocaleString()}+ pages/mo — +$${SCRAPER_PAGE_PRO_PRICE} Pro +$${SCRAPER_PAGE_ENT_PRICE} Ent = $${SCRAPER_PAGE_PRO_PRICE + SCRAPER_PAGE_ENT_PRICE}/mo total`}
            </span>
          </div>
          <div className="mt-1 text-xs text-slate-400">
            Auto-refresh:{" "}
            <span className={`font-medium ${KNOWLEDGE_TIER_COLORS[crawlTier]}`}>
              {crawlTier === "Basic" ? "Monthly (30 days)" : crawlTier === "Pro" ? "Weekly" : "Daily"}
            </span>
            {" "}— keeps bot answers current without manual re-crawls.
            {crawlTier === "Basic" && <span className="text-slate-500"> Upgrade to Pro for weekly refresh.</span>}
          </div>
          {platform !== "none" && (
            <div className="mt-1 text-xs text-blue-400">
              {COMMERCE_PLATFORM_LABELS[platform]} customers typically need 1,000–5,000 pages/mo to cover product catalog + category pages.
            </div>
          )}
        </div>

        {/* PDF / Document Pages */}
        {knowledge.hasPdfUploads && (
          <div>
            <label className={label}>Uploaded Document Pages / Month (estimate)</label>
            <input
              className={input}
              type="number"
              min={0}
              step={100}
              value={knowledge.pdfPageCount}
              onChange={e => onChange({ pdfPageCount: Math.max(0, Number(e.target.value)) })}
            />
            <div className="mt-1 text-xs text-slate-400">
              Estimate total pages across all files uploaded each month.
              <span className="text-slate-500 ml-1">1 page = 1 document page (PDF page, Word page, spreadsheet sheet).</span>
            </div>
            {knowledge.pdfPageCount > 50000 && (
              <div className="mt-1 text-xs text-amber-400 font-medium">
                ⚠ {knowledge.pdfPageCount.toLocaleString()} pages/mo exceeds Enterprise limits (50,000/mo). This customer requires a custom contract — flag for account management before quoting.
              </div>
            )}
            {knowledge.pdfPageCount > 5000 && knowledge.pdfPageCount <= 50000 && (
              <div className="mt-1 text-xs text-purple-400">Enterprise-level document volume — verify largest single file is under 2,000 pages.</div>
            )}
            {knowledge.pdfPageCount > 500 && knowledge.pdfPageCount <= 5000 && (
              <div className="mt-1 text-xs text-blue-400">Pro-level document volume — verify no single file exceeds 500 pages.</div>
            )}
          </div>
        )}

        {/* Document add-ons */}
        <div>
          <div className={label}>Document Indexing</div>
          <div className="space-y-3">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={knowledge.hasPdfUploads}
                onChange={e => onChange({ hasPdfUploads: e.target.checked })}
                className="mt-0.5 w-4 h-4 accent-blue-500"
              />
              <div>
                <div className="text-white text-sm font-medium">PDF / Word / CSV Document Indexing <span className="text-amber-400 text-xs font-normal">+$150/mo</span></div>
                <div className="text-slate-400 text-xs">Upload spec sheets, manuals, price lists, or contracts. AI reads and references them during conversations.</div>
                <div className="text-slate-500 text-xs mt-0.5">
                  <span className="font-medium text-slate-400">What is a page?</span>{" "}
                  1 page = 1 document page (PDF page, Word page, spreadsheet sheet).
                  Each plan has a monthly page budget and a per-file cap — pages beyond the cap are not indexed.
                </div>
              </div>
            </label>
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={knowledge.hasOneDriveIntegration}
                onChange={e => onChange({ hasOneDriveIntegration: e.target.checked })}
                className="mt-0.5 w-4 h-4 accent-blue-500"
              />
              <div>
                <div className="text-white text-sm font-medium">OneDrive / SharePoint Auto-Sync <span className="text-amber-400 text-xs font-normal">+$200/mo</span></div>
                <div className="text-slate-400 text-xs">Auto-index your Microsoft 365 document library. Files stay in sync — no manual uploads needed.</div>
              </div>
            </label>
          </div>
        </div>

        {/* Quick reference table */}
        <div className="bg-slate-900 border border-slate-700 rounded p-3 text-xs text-slate-400">
          <div className="text-slate-300 font-semibold mb-2">Knowledge Limits by Plan</div>
          <div className="grid grid-cols-3 gap-2 text-center">
            {[
              { name: "Basic",      articles: "200",   webPages: "500/mo",    docPages: "500/mo",    perFile: "200 pg max", color: "text-green-400" },
              { name: "Pro",        articles: "1,000", webPages: "5,000/mo",  docPages: "5,000/mo",  perFile: "500 pg max", color: "text-blue-400" },
              { name: "Enterprise", articles: "5,000", webPages: "50,000/mo", docPages: "50,000/mo", perFile: "2,000 pg max", color: "text-purple-400" },
            ].map(t => (
              <div key={t.name} className="bg-slate-800 rounded p-2">
                <div className={`font-semibold ${t.color}`}>{t.name}</div>
                <div className="mt-1">{t.articles} articles</div>
                <div>{t.webPages} web pages</div>
                <div>{t.docPages} doc pages</div>
                <div className="mt-1 text-slate-500">{t.perFile}</div>
              </div>
            ))}
          </div>
          <div className="mt-2 text-slate-500">
            <span className="font-medium text-slate-400">Page definitions:</span>{" "}
            Web page = 1 URL crawled. Document page = 1 PDF/Word/spreadsheet page. Both count toward the monthly budget.
            Per-file cap = max pages indexed from any single file. Customers above Enterprise limits need a custom contract.
          </div>
          <div className="mt-1 text-slate-500">Add-on pricing is cumulative: Enterprise tier = Pro add-on ($100) + Enterprise upgrade ($150) = $250/mo per dimension.</div>
        </div>
      </div>
    </div>
  );
}

// ── Commerce Knowledge Reference Panel ───────────────────────────────────

function CommerceKnowledgePanel({ platform }: { platform: CommercePlatform }) {
  const [expanded, setExpanded] = React.useState(false);
  const label = COMMERCE_PLATFORM_LABELS[platform];
  const skills = COMMERCE_PLATFORM_SKILLS[platform];

  return (
    <div className="bg-slate-800 border border-slate-700 rounded-xl overflow-hidden">
      <button
        className="w-full px-5 py-3 flex items-center justify-between text-left hover:bg-slate-750 transition-colors"
        onClick={() => setExpanded(v => !v)}
        aria-expanded={expanded}
      >
        <div className="flex items-center gap-2">
          <span className="text-green-400 text-sm font-semibold">{label} — Sales Reference ({skills} skills)</span>
          <span className="text-xs bg-green-900/40 text-green-300 border border-green-700 rounded px-1.5 py-0.5">Internal</span>
        </div>
        <span className="text-slate-400 text-sm">{expanded ? "\u25B2" : "\u25BC"}</span>
      </button>

      {expanded && (
        <div className="px-5 pb-5 space-y-5 text-xs text-slate-300 border-t border-slate-700 pt-4">
          <section>
            <h3 className="text-white font-semibold text-sm mb-2">How Skill Packages Work</h3>
            <div className="space-y-2 text-slate-400">
              <p>Each skill is an AI tool the bot calls automatically during conversation. The customer never configures tools — they just connect their store and the bot starts using them.</p>
              <p><strong className="text-white">Setup complexity:</strong> Cart recovery, abandoned cart, coupon application — these all work through the BigCommerce API automatically. No email campaign setup needed. The bot detects intent ("I left something in my cart") and calls the right API. The heavy lift is <em>our engineering</em>, not the customer's configuration.</p>
              <p><strong className="text-white">Why we charge per package:</strong> Each skill costs us AI inference ($0.002–$0.01 per call) plus API calls to the commerce platform. More skills = more cost per conversation. Packages let customers pay for what they use.</p>
            </div>
          </section>

          <section>
            <h3 className="text-white font-semibold text-sm mb-2">Package Breakdown</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-xs border-collapse">
                <thead>
                  <tr className="border-b border-slate-700">
                    <th className="text-left text-slate-400 py-1 pr-3">Package</th>
                    <th className="text-left text-slate-400 py-1 pr-3">Skills</th>
                    <th className="text-left text-slate-400 py-1 pr-3">Retail</th>
                    <th className="text-left text-slate-400 py-1 pr-3">Marked Down</th>
                    <th className="text-left text-slate-400 py-1">Best For</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-slate-700/50">
                    <td className="py-1 pr-3 text-green-300">Core</td>
                    <td className="py-1 pr-3 font-mono">10</td>
                    <td className="py-1 pr-3 font-mono">{fmt(COMMERCE_PACKAGES.core.retail)}</td>
                    <td className="py-1 pr-3 font-mono text-green-400">{fmt(COMMERCE_PACKAGES.core.marked)}</td>
                    <td className="py-1 text-slate-400">Every store — product + order answers</td>
                  </tr>
                  <tr className="border-b border-slate-700/50">
                    <td className="py-1 pr-3 text-blue-300">Commerce</td>
                    <td className="py-1 pr-3 font-mono">10</td>
                    <td className="py-1 pr-3 font-mono">{fmt(COMMERCE_PACKAGES.commerce.retail)}</td>
                    <td className="py-1 pr-3 font-mono text-green-400">{fmt(COMMERCE_PACKAGES.commerce.marked)}</td>
                    <td className="py-1 text-slate-400">Cart recovery + checkout conversion</td>
                  </tr>
                  <tr className="border-b border-slate-700/50">
                    <td className="py-1 pr-3 text-amber-300">B2B &amp; Ops</td>
                    <td className="py-1 pr-3 font-mono">8</td>
                    <td className="py-1 pr-3 font-mono">{fmt(COMMERCE_PACKAGES.b2bOps.retail)}</td>
                    <td className="py-1 pr-3 font-mono text-green-400">{fmt(COMMERCE_PACKAGES.b2bOps.marked)}</td>
                    <td className="py-1 text-slate-400">Wholesale, B2B, institutional buyers</td>
                  </tr>
                  <tr>
                    <td className="py-1 pr-3 text-purple-300">Logistics</td>
                    <td className="py-1 pr-3 font-mono">5</td>
                    <td className="py-1 pr-3 font-mono">{fmt(COMMERCE_PACKAGES.logistics.retail)}</td>
                    <td className="py-1 pr-3 font-mono text-green-400">{fmt(COMMERCE_PACKAGES.logistics.marked)}</td>
                    <td className="py-1 text-slate-400">Shipping, gifting, promos</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div className="mt-2 bg-green-900/20 border border-green-700 rounded p-2">
              <strong className="text-green-400">All-In Bundle:</strong> {fmt(COMMERCE_ALL_IN_MARKED)}/mo (retail {fmt(COMMERCE_ALL_IN_RETAIL)}) — all {skills} skills, maximum flexibility.
            </div>
          </section>

          <section>
            <h3 className="text-white font-semibold text-sm mb-2">Marketplace vs Direct Pricing</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-xs border-collapse">
                <thead>
                  <tr className="border-b border-slate-700">
                    <th className="text-left text-slate-400 py-1 pr-3">Tier</th>
                    <th className="text-left text-slate-400 py-1 pr-3">Marketplace</th>
                    <th className="text-left text-slate-400 py-1 pr-3">Seats</th>
                    <th className="text-left text-slate-400 py-1 pr-3">Conv/mo</th>
                    <th className="text-left text-slate-400 py-1">Skills</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { tier: "Starter",   price: "$97",  seats: "1", conv: "500",    skills: "10 (Core)" },
                    { tier: "Growth",    price: "$297", seats: "3", conv: "2,000",  skills: "20 (Core+Commerce)" },
                    { tier: "Pro",       price: "$497", seats: "5", conv: "5,000",  skills: "28 (Core+Commerce+B2B)" },
                    { tier: "Scale",     price: "$997", seats: "8", conv: "15,000", skills: `All ${skills}` },
                  ].map(r => (
                    <tr key={r.tier} className="border-b border-slate-700/50">
                      <td className="py-1 pr-3 text-white">{r.tier}</td>
                      <td className="py-1 pr-3 font-mono">{r.price}/mo</td>
                      <td className="py-1 pr-3">{r.seats}</td>
                      <td className="py-1 pr-3">{r.conv}</td>
                      <td className="py-1">{r.skills}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-2 text-slate-400">
              Marketplace takes 20%. Direct deals use per-package pricing above — higher margin, more flexible.
              For existing customers like MF Athletic, keep their base contract and add packages as add-ons.
            </p>
          </section>
        </div>
      )}
    </div>
  );
}

// ── Step 6: Add-ons ───────────────────────────────────────────────────────

function StepAddons({
  addons, channels, l3Orchestration,
  extraBlocks, extraWorkflowAiBlocks, extraMcpBlocks, extraSmsBlocks, extraIvrBlocks,
  onChange, onChangeL3, onChangeBlocks,
  onChangeWorkflowAi, onChangeMcp, onChangeSms, onChangeIvr,
}: {
  addons: AddOns;
  channels: Channels;
  l3Orchestration: boolean;
  extraBlocks: number;
  extraWorkflowAiBlocks: number;
  extraMcpBlocks: number;
  extraSmsBlocks: number;
  extraIvrBlocks: number;
  onChange: (p: Partial<AddOns>) => void;
  onChangeL3: (v: boolean) => void;
  onChangeBlocks: (v: number) => void;
  onChangeWorkflowAi: (v: number) => void;
  onChangeMcp: (v: number) => void;
  onChangeSms: (v: number) => void;
  onChangeIvr: (v: number) => void;
}) {
  return (
    <div className={card}>
      <h2 className="text-white font-semibold mb-1">Step 6 — Add-ons</h2>
      <p className="text-slate-400 text-xs mb-4">Select any additional products or compliance needs.</p>

      <div className="space-y-5">
        {/* Language */}
        <div>
          <label className={label}>Language Packs</label>
          <select className={select} value={addons.languagePack}
            onChange={e => onChange({ languagePack: Number(e.target.value) as AddOns["languagePack"] })}>
            <option value={0}>English only (included)</option>
            <option value={1}>+1 Language — $300/mo</option>
            <option value={3}>+3 Languages — $600/mo</option>
            <option value={5}>+5 Languages — $900/mo</option>
          </select>
        </div>

        {/* IVR add-ons */}
        {channels.ivr && (
          <div className="space-y-4">
            <div>
              <label className={label}>IVR Package Tier</label>
              <select className={select} value={addons.ivrTier}
                onChange={e => onChange({ ivrTier: e.target.value as IvrTier })}>
                {(Object.entries(IVR_TIERS) as [IvrTier, typeof IVR_TIERS[IvrTier]][]).map(([key, t]) => (
                  <option key={key} value={key}>
                    {t.name} — ${t.price.toLocaleString()}/mo · {t.includedMin.toLocaleString()} AI min · {t.blurb}
                  </option>
                ))}
              </select>
              <div className="mt-2 text-xs text-slate-400 space-y-1">
                <div>Requires: <span className="text-amber-300">{IVR_TIERS[addons.ivrTier].minPlan}</span> base plan</div>
                {IVR_TIERS[addons.ivrTier].liveTransfer && <div>✓ Live agent transfer — {IVR_TIERS[addons.ivrTier].includedBridgeMin.toLocaleString()} bridge min/mo included, then ${IVR_BRIDGE_BLOCK_PRICE}/{IVR_BRIDGE_BLOCK_SIZE.toLocaleString()} min overage</div>}
                {!IVR_TIERS[addons.ivrTier].liveTransfer && <div className="text-amber-400">✗ Live transfer not included — upgrade to IVR Professional to add agent handoff</div>}
                {IVR_TIERS[addons.ivrTier].screenPop && <div>✓ CRM screen pop on inbound calls (HubSpot, Salesforce, Dynamics)</div>}
                {IVR_TIERS[addons.ivrTier].outbound && <div>✓ Outbound AI campaigns — ${IVR_OUTBOUND_BLOCK_PRICE}/{IVR_OUTBOUND_BLOCK_SIZE.toLocaleString()} min block</div>}
                <div>✓ {IVR_TIERS[addons.ivrTier].languages === 1 ? "English only" : `Multilingual — up to ${IVR_TIERS[addons.ivrTier].languages} languages`}</div>
                <div className="text-blue-300">ElevenLabs voice included. Extra AI min overage: ${IVR_EXTRA_MIN_BLOCK_PRICE}/{IVR_EXTRA_MIN_BLOCK_SIZE.toLocaleString()} min block.</div>
              </div>
            </div>
          </div>
        )}

        {/* Compliance */}
        <div>
          <div className={label}>Compliance</div>
          <div className="space-y-2">
            {[
              { key: "hipaa",         label: "HIPAA — $2,000/mo" },
              { key: "pci",           label: "PCI DSS — $1,500/mo" },
              { key: "dataResidency", label: "Single-Region Data Residency — $500/mo" },
            ].map(c => (
              <label key={c.key} className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={!!addons[c.key as keyof AddOns]}
                  onChange={e => onChange({ [c.key]: e.target.checked } as Partial<AddOns>)}
                  className="w-4 h-4 accent-blue-500" />
                <span className="text-slate-300 text-sm">{c.label}</span>
              </label>
            ))}
          </div>
        </div>

        {/* L3 orchestration (manual override) */}
        <div>
          <div className={label}>AI Orchestration</div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={l3Orchestration}
              onChange={e => onChangeL3(e.target.checked)}
              className="w-4 h-4 accent-blue-500" />
            <span className="text-slate-300 text-sm">L3 Orchestration Upgrade — $1,500/mo</span>
          </label>
        </div>

        {/* Integrations */}
        <div>
          <div className={label}>Integrations</div>
          <div className="space-y-3">
            <div>
              <label className="text-slate-400 text-xs mb-1 block">Pre-built (Shopify, HubSpot, NetSuite, Dynamics) — avg $299/mo each</label>
              <input className={input} type="number" min={0} max={20} value={addons.preBuiltIntegrationCount}
                onChange={e => onChange({ preBuiltIntegrationCount: Math.max(0, Number(e.target.value)) })} />
            </div>
            <div>
              <label className="text-slate-400 text-xs mb-1 block">Complex / Legacy integrations — avg $400/mo each</label>
              <input className={input} type="number" min={0} max={20} value={addons.complexIntegrationCount}
                onChange={e => onChange({ complexIntegrationCount: Math.max(0, Number(e.target.value)) })} />
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={addons.standardApi}
                onChange={e => onChange({ standardApi: e.target.checked })}
                className="w-4 h-4 accent-blue-500" />
              <span className="text-slate-300 text-sm">Standard API — $299/mo</span>
            </label>
          </div>
        </div>

        {/* AI Compose Email */}
        <div>
          <div className={label}>AI Compose Email</div>
          <div className="space-y-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={addons.aiEmailAddOn}
                onChange={e => onChange({ aiEmailAddOn: e.target.checked })}
                className="w-4 h-4 accent-blue-500" />
              <span className="text-slate-300 text-sm">
                AI Compose Email — $149/mo
                <span className="text-slate-500 text-xs ml-2">(Included free in Professional+)</span>
              </span>
            </label>
            {addons.aiEmailAddOn && (
              <div className="ml-6 text-xs text-blue-400 border-l border-slate-700 pl-4">
                Workflow node that describes an email in plain language — Claude Haiku writes it, personalises it from conversation context, and sends it via ACS automatically. 200 AI-composed emails/day. No hard recipient restrictions (unlike competitors).
              </div>
            )}
          </div>
        </div>

        {/* Recruiting Bot */}
        <div>
          <div className={label}>Recruiting Bot</div>
          <div className="space-y-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={addons.recruitingBot}
                onChange={e => onChange({ recruitingBot: e.target.checked, ...(e.target.checked ? {} : { recruitingBotAtsIntegration: false, recruitingBotGlobalHiring: false, recruitingBotEeoc: false, recruitingBotCalendarScreening: false }) })}
                className="w-4 h-4 accent-blue-500" />
              <span className="text-slate-300 text-sm">Recruiting Bot — Candidate Intake AI — $500/mo <span className="text-slate-500 text-xs">(Paradox charges $12K–$20K/mo for this alone)</span></span>
            </label>
            {addons.recruitingBot && (
              <div className="ml-6 space-y-2 border-l border-slate-700 pl-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={addons.recruitingBotAtsIntegration}
                    onChange={e => onChange({ recruitingBotAtsIntegration: e.target.checked })}
                    className="w-4 h-4 accent-blue-500" />
                  <span className="text-slate-300 text-sm">ATS API Integration (SuccessFactors OData / Workday REST) — $400/mo</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={addons.recruitingBotGlobalHiring}
                    onChange={e => onChange({ recruitingBotGlobalHiring: e.target.checked })}
                    className="w-4 h-4 accent-blue-500" />
                  <span className="text-slate-300 text-sm">Global Hiring Premium (multi-country, multi-language feed) — $600/mo</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={addons.recruitingBotEeoc}
                    onChange={e => onChange({ recruitingBotEeoc: e.target.checked })}
                    className="w-4 h-4 accent-blue-500" />
                  <span className="text-slate-300 text-sm">EEOC/EEO Compliance Module (voluntary self-ID collection + CSV report) — $300/mo</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={addons.recruitingBotCalendarScreening}
                    onChange={e => onChange({ recruitingBotCalendarScreening: e.target.checked })}
                    className="w-4 h-4 accent-blue-500" />
                  <span className="text-slate-300 text-sm">Interview Scheduling (auto-book screens via Calendly / Outlook) — $250/mo</span>
                </label>
                <div className="text-xs text-blue-400 mt-1">
                  Full stack: $2,050/mo vs. Paradox $12,000–$20,000/mo for the same capability.
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Extra conversation blocks */}
        <div>
          <label className={label}>Extra Conversation Blocks (+2,500 conversations @ $375 each)</label>
          <input className={input} type="number" min={0} max={100} value={extraBlocks}
            onChange={e => onChangeBlocks(Math.max(0, Number(e.target.value)))} />
          {extraBlocks > 0 && (
            <div className="text-xs text-blue-400 mt-1">
              Adds {(extraBlocks * 2500).toLocaleString()} conversations/mo — ${(extraBlocks * 375).toLocaleString()}/mo
            </div>
          )}
        </div>

        {/* Enterprise modular volume bundles */}
        <div className="border border-purple-700/50 rounded-lg p-4 bg-purple-950/20">
          <div className="text-purple-300 text-xs font-bold uppercase tracking-wide mb-3">Enterprise Volume Bundles — stack as many as needed</div>
          <div className="space-y-3">
            <div>
              <label className={label}>Workflow AI bundles (+5,000 handoffs @ $500/bundle)</label>
              <input className={input} type="number" min={0} max={50} value={extraWorkflowAiBlocks}
                onChange={e => onChangeWorkflowAi(Math.max(0, Number(e.target.value)))} />
              {extraWorkflowAiBlocks > 0 && (
                <div className="text-xs text-purple-300 mt-1">
                  +{(extraWorkflowAiBlocks * ENT_WORKFLOW_AI_BLOCK_SIZE).toLocaleString()} workflow AI handoffs/mo — ${(extraWorkflowAiBlocks * ENT_WORKFLOW_AI_BLOCK_PRICE).toLocaleString()}/mo
                </div>
              )}
            </div>
            <div>
              <label className={label}>MCP skill call bundles (+25,000 calls @ $400/bundle)</label>
              <input className={input} type="number" min={0} max={50} value={extraMcpBlocks}
                onChange={e => onChangeMcp(Math.max(0, Number(e.target.value)))} />
              {extraMcpBlocks > 0 && (
                <div className="text-xs text-purple-300 mt-1">
                  +{(extraMcpBlocks * ENT_MCP_BLOCK_SIZE).toLocaleString()} MCP calls/mo — ${(extraMcpBlocks * ENT_MCP_BLOCK_PRICE).toLocaleString()}/mo
                </div>
              )}
            </div>
            <div>
              <label className={label}>SMS bundles (+2,000 messages @ $75/bundle)</label>
              <input className={input} type="number" min={0} max={100} value={extraSmsBlocks}
                onChange={e => onChangeSms(Math.max(0, Number(e.target.value)))} />
              {extraSmsBlocks > 0 && (
                <div className="text-xs text-purple-300 mt-1">
                  +{(extraSmsBlocks * ENT_SMS_BLOCK_SIZE).toLocaleString()} outbound SMS/mo — ${(extraSmsBlocks * ENT_SMS_BLOCK_PRICE).toLocaleString()}/mo
                </div>
              )}
            </div>
            <div>
              <label className={label}>IVR minute bundles (+500 min @ $100/bundle)</label>
              <input className={input} type="number" min={0} max={100} value={extraIvrBlocks}
                onChange={e => onChangeIvr(Math.max(0, Number(e.target.value)))} />
              {extraIvrBlocks > 0 && (
                <div className="text-xs text-purple-300 mt-1">
                  +{(extraIvrBlocks * ENT_IVR_BLOCK_SIZE).toLocaleString()} IVR minutes/mo — ${(extraIvrBlocks * ENT_IVR_BLOCK_PRICE).toLocaleString()}/mo
                </div>
              )}
            </div>
          </div>
          <div className="text-slate-500 text-xs mt-3">These are list prices. Enterprise contracts typically negotiate a volume discount at 10–20% for pre-committed annual bundles.</div>
        </div>
      </div>
    </div>
  );
}

// ── Step 6: Contract Term ─────────────────────────────────────────────────

function StepContractTerm({ term, onChange }: { term: ContractTerm; onChange: (t: ContractTerm) => void }) {
  const terms: ContractTerm[] = ["monthly", "annual", "2year", "3year"];
  return (
    <div className={card}>
      <h2 className="text-white font-semibold mb-1">Step 7 — Contract Term</h2>
      <p className="text-slate-400 text-xs mb-4">Longer terms unlock deeper discounts off monthly pricing.</p>
      <div className="space-y-3">
        {terms.map(t => (
          <label key={t} className={`flex items-center gap-3 cursor-pointer rounded-lg border p-3 transition-colors ${term === t ? "border-blue-500 bg-blue-950/30" : "border-slate-700 hover:border-slate-500"}`}>
            <input type="radio" name="contractTerm" value={t} checked={term === t}
              onChange={() => onChange(t)} className="accent-blue-500" />
            <div>
              <div className="text-white text-sm font-medium">{CONTRACT_LABELS[t]}</div>
              {CONTRACT_DISCOUNTS[t] > 0 && (
                <div className="text-green-400 text-xs">{(CONTRACT_DISCOUNTS[t] * 100).toFixed(0)}% discount applied to all monthly line items</div>
              )}
            </div>
          </label>
        ))}
      </div>
    </div>
  );
}

// ── Quote panel ───────────────────────────────────────────────────────────

function QuotePanel({
  quote, channels, tier, contractTerm, customer,
}: {
  quote: QuoteResult;
  channels: Channels;
  tier: typeof TIERS[0];
  contractTerm: ContractTerm;
  customer: CustomerInfo;
}) {
  return (
    <div className="bg-slate-800 border border-slate-700 rounded-xl overflow-hidden">
      {/* Header */}
      <div className="bg-slate-700 px-5 py-3 flex items-center justify-between">
        <div>
          <div className="text-white font-semibold text-sm">Live Quote</div>
          {customer.companyName && <div className="text-slate-400 text-xs">{customer.companyName}</div>}
        </div>
        <div className="text-xs text-slate-400 bg-slate-800 rounded px-2 py-1">
          {CONTRACT_LABELS[contractTerm].split(" — ")[0]}
        </div>
      </div>

      {/* IVR floor warning */}
      {quote.ivrFloorWarning && (
        <div className="bg-red-900/40 border-b border-red-700 px-5 py-3">
          <div className="text-red-400 text-xs font-bold uppercase tracking-wide mb-0.5">IVR Floor Check — FAILED</div>
          <div className="text-red-300 text-xs">
            IVR minimum is {fmt(IVR_FLOOR)}/mo. Current IVR components are {fmt(quote.monthlySubtotal)} — need {fmt(quote.ivrFloorShortfall)} more to reach floor.
            Add carrier minutes or adjust tier.
          </div>
        </div>
      )}

      {/* Line items */}
      <div className="px-5 py-4 space-y-2 max-h-96 overflow-y-auto">
        {quote.lines.length === 0 && (
          <div className="text-slate-500 text-xs text-center py-4">Complete the wizard to see line items.</div>
        )}
        {quote.lines.map((line, i) => (
          <div key={i} className="flex justify-between items-start gap-2">
            <div className="flex-1 min-w-0">
              <div className={`text-xs leading-tight ${line.isWarning ? "text-amber-400" : "text-slate-300"}`}>
                {line.label}
                {line.isPassThrough && <span className="text-slate-500 ml-1">(pass-through)</span>}
              </div>
              {line.note && <div className="text-slate-500 text-xs">{line.note}</div>}
            </div>
            <div className={`text-xs font-mono shrink-0 ${line.isWarning ? "text-amber-400" : "text-white"}`}>
              {fmt(line.amount)}
            </div>
          </div>
        ))}
      </div>

      {/* Totals */}
      <div className="border-t border-slate-700 px-5 py-4 space-y-2">
        <div className="flex justify-between text-sm">
          <span className="text-slate-400">Monthly subtotal</span>
          <span className="text-white font-mono">{fmt(quote.monthlySubtotal)}</span>
        </div>
        {quote.contractDiscountPct > 0 && (
          <div className="flex justify-between text-sm">
            <span className="text-green-400">Contract discount ({quote.discountPct.toFixed(0)}%)</span>
            <span className="text-green-400 font-mono">-{fmt(quote.monthlySubtotal * quote.contractDiscountPct)}</span>
          </div>
        )}
        <div className="flex justify-between text-sm font-semibold border-t border-slate-700 pt-2">
          <span className="text-white">Monthly (after discount)</span>
          <span className="text-blue-400 font-mono text-base">{fmt(quote.monthlyAfterDiscount)}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-slate-400">Annual total</span>
          <span className="text-white font-mono">{fmt(quote.annualAfterDiscount)}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-slate-400">Setup fee (20%, min $3K)</span>
          <span className="text-white font-mono">{fmt(quote.setupFee)}</span>
        </div>
        <div className="flex justify-between text-base font-bold border-t border-slate-600 pt-2">
          <span className="text-white">Year 1 Total</span>
          <span className="text-amber-400 font-mono">{fmt(quote.year1Total)}</span>
        </div>
      </div>

      {/* Tier badge */}
      <div className="border-t border-slate-700 px-5 py-3 flex items-center justify-between">
        <div className="text-slate-400 text-xs">Auto-selected tier</div>
        <div className="text-xs font-semibold text-blue-300 bg-blue-900/40 border border-blue-700 rounded px-2 py-0.5">
          {quote.tierName} — {quote.tierConversations.toLocaleString()} conv/mo
        </div>
      </div>

      {/* AI & Automation hard limits — MUST be visible before signing */}
      {(() => {
        const lim = PLAN_AI_LIMITS[tier.name];
        if (!lim) return null;
        const fmt2 = (v: number | string) => typeof v === "number" ? v.toLocaleString() : v;
        const HARD  = "Hard block at limit";
        const SOFT  = "Overage billing applies";
        const rows: [string, string, string][] = [
          ["Conversations/mo",         fmt2(lim.conversations),   SOFT],
          ["Workflow AI handoffs/mo",  fmt2(lim.workflowAiCalls), HARD],
          ["MCP skill calls/mo",       fmt2(lim.mcpCalls),        lim.mcpCalls === "n/a" ? "Requires Professional+" : HARD],
          ["Workflow runs/mo",         lim.workflowRuns === 0 ? "Negotiated" : fmt2(lim.workflowRuns), HARD],
          ["SMS outbound/mo",          "Plan included",            SOFT],
          ["IVR minutes/mo",           "Plan included",            SOFT],
          ["AI tokens/mo",             `${lim.aiTokensM}M tokens`, HARD],
        ];
        return (
          <div className="border-t border-amber-700/50 px-5 py-4 bg-amber-950/30">
            <div className="text-amber-400 text-xs font-bold uppercase tracking-wide mb-2">
              AI & Automation Hard Limits — {tier.name}
            </div>
            <div className="text-amber-300/70 text-xs mb-3">
              All limits enforced with <span className="text-amber-300 font-semibold">hard block</span> (OverageBehavior=block). Customer stops working at limit — no grace. Include overage pricing or uplift in contract or they will call support.
            </div>
            <table className="w-full text-xs">
              <thead>
                <tr className="text-slate-500 border-b border-slate-700">
                  <th className="text-left pb-1 font-normal">Feature</th>
                  <th className="text-right pb-1 font-normal">Monthly limit</th>
                  <th className="text-right pb-1 font-normal">At limit</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(([label, val, behavior]) => (
                  <tr key={label} className="border-b border-slate-800">
                    <td className="py-1 text-slate-300">{label}</td>
                    <td className="py-1 text-right font-mono text-white">{val}</td>
                    <td className={`py-1 text-right ${behavior === SOFT ? "text-yellow-400" : behavior.startsWith("Requires") ? "text-slate-500" : "text-red-400"}`}>{behavior}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="mt-2 text-slate-500 text-xs">
              Override any limit via SuperAdmin → Subscriptions → {tier.name} customer. Enterprise: negotiate limits in contract and set manually.
            </div>
          </div>
        );
      })()}
    </div>
  );
}

// ── Flag for AM ───────────────────────────────────────────────────────────

function FlagForAM({ notes, newNote, onNewNote, onAdd, onRemove }: {
  notes: FlagNote[];
  newNote: string;
  onNewNote: (v: string) => void;
  onAdd: () => void;
  onRemove: (i: number) => void;
}) {
  return (
    <div className={card}>
      <h2 className="text-white font-semibold mb-1 flex items-center gap-2">
        <span>Flag for Account Manager</span>
        {notes.length > 0 && (
          <span className="text-xs bg-amber-800/50 text-amber-400 border border-amber-700 rounded-full px-2 py-0.5">{notes.length}</span>
        )}
      </h2>
      <p className="text-slate-400 text-xs mb-3">Notes saved in browser. Use to flag unknowns, exceptions, or items to confirm with AM.</p>

      {notes.length > 0 && (
        <div className="space-y-2 mb-3">
          {notes.map((n, i) => (
            <div key={i} className="bg-slate-900 border border-slate-700 rounded p-2 flex items-start gap-2">
              <div className="flex-1 min-w-0">
                <div className="text-slate-300 text-xs">{n.note}</div>
                <div className="text-slate-600 text-xs mt-0.5">{n.savedAt}</div>
              </div>
              <button onClick={() => onRemove(i)} className="text-slate-500 hover:text-red-400 text-xs shrink-0 transition-colors" aria-label="Remove note">✕</button>
            </div>
          ))}
        </div>
      )}

      <div className="flex gap-2">
        <textarea
          className={`flex-1 ${input} resize-none`}
          rows={2}
          value={newNote}
          onChange={e => onNewNote(e.target.value)}
          placeholder="E.g.: Confirm HIPAA scope. IVR call volume unverified — get from IT. Deal has competitive pressure from Genesys."
          onKeyDown={e => { if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) onAdd(); }}
        />
        <button onClick={onAdd} className={btnPrimary + " self-end"} disabled={!newNote.trim()}>Add</button>
      </div>
      <div className="text-slate-600 text-xs mt-1">Ctrl+Enter to add quickly</div>
    </div>
  );
}

// ── Proposal modal ────────────────────────────────────────────────────────

function ProposalModal({ text, copied, onCopy, onPrint, onClose, state }: {
  text: string;
  copied: boolean;
  onCopy: () => void;
  onPrint: () => void;
  onClose: () => void;
  state: WizardState;
}) {
  const quote = calculateQuote(state);
  const unknowns: string[] = [];
  if (!state.customer.companyName)       unknowns.push("Company name not entered");
  if (!state.customer.vertical)          unknowns.push("Industry/vertical not selected");
  if (state.channels.ivr && state.volume.monthlyCallsIfIvr === 0) unknowns.push("IVR monthly call minutes — unknown");
  if (state.volume.monthlyConversations === 0) unknowns.push("Monthly conversation volume — confirm with customer");
  if (state.journey.level !== "L1" && state.journey.integrationCount === 0) unknowns.push("Integration count not specified for L2/L3 journey");

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70">
      <div className="bg-slate-800 border border-slate-700 rounded-xl w-full max-w-3xl max-h-screen overflow-y-auto">
        {/* Modal header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-700 sticky top-0 bg-slate-800 z-10">
          <h2 className="text-white font-semibold text-lg">Proposal Summary</h2>
          <div className="flex items-center gap-2">
            <button onClick={onCopy} className={btnSecondary}>
              {copied ? "Copied!" : "Copy to Clipboard"}
            </button>
            <button onClick={onPrint} className={btnSecondary}>Print</button>
            <button onClick={onClose} className="text-slate-400 hover:text-white transition-colors text-xl ml-2" aria-label="Close">✕</button>
          </div>
        </div>

        <div className="p-6 space-y-5">
          {/* Customer */}
          {state.customer.companyName && (
            <div>
              <div className="text-slate-400 text-xs uppercase tracking-wide mb-1">Prepared For</div>
              <div className="text-white font-semibold">{state.customer.companyName}</div>
              {state.customer.contactName && <div className="text-slate-300 text-sm">{state.customer.contactName} — {state.customer.contactEmail}</div>}
              {state.customer.vertical && <div className="text-slate-400 text-sm">{state.customer.vertical}</div>}
            </div>
          )}

          {/* IVR warning */}
          {quote.ivrFloorWarning && (
            <div className="bg-red-900/30 border border-red-700 rounded-lg p-4">
              <div className="text-red-400 font-semibold text-sm">IVR Minimum Not Met</div>
              <div className="text-red-300 text-xs mt-1">
                IVR requires a minimum of {fmt(IVR_FLOOR)}/mo. Current total is below floor by {fmt(quote.ivrFloorShortfall)}.
                Review carrier minutes selection and confirm call volume with customer before presenting.
              </div>
            </div>
          )}

          {/* Line items */}
          <div>
            <div className="text-slate-400 text-xs uppercase tracking-wide mb-2">Monthly Investment Breakdown</div>
            <div className="bg-slate-900 rounded-lg overflow-hidden">
              {quote.lines.map((line, i) => (
                <div key={i} className={`flex justify-between px-4 py-2 text-sm ${i % 2 === 0 ? "bg-slate-900" : "bg-slate-800/50"}`}>
                  <span className="text-slate-300">
                    {line.label}
                    {line.isPassThrough && <span className="text-slate-500 ml-1 text-xs">(pass-through)</span>}
                    {line.note && <span className="text-slate-500 ml-1 text-xs">({line.note})</span>}
                  </span>
                  <span className="text-white font-mono">{fmt(line.amount)}</span>
                </div>
              ))}
              {/* Subtotal */}
              <div className="flex justify-between px-4 py-2 border-t border-slate-700 font-semibold text-sm">
                <span className="text-slate-300">Monthly Subtotal</span>
                <span className="text-white font-mono">{fmt(quote.monthlySubtotal)}</span>
              </div>
              {quote.contractDiscountPct > 0 && (
                <div className="flex justify-between px-4 py-2 text-sm">
                  <span className="text-green-400">{CONTRACT_LABELS[state.contractTerm]} Discount</span>
                  <span className="text-green-400 font-mono">-{fmt(quote.monthlySubtotal * quote.contractDiscountPct)}</span>
                </div>
              )}
              <div className="flex justify-between px-4 py-2 text-sm bg-slate-700/50">
                <span className="text-white font-semibold">Monthly After Discount</span>
                <span className="text-blue-400 font-mono font-bold text-base">{fmt(quote.monthlyAfterDiscount)}</span>
              </div>
            </div>
          </div>

          {/* Annual summary */}
          <div className="grid grid-cols-3 gap-3">
            {[
              { label: "Annual Total", value: fmt(quote.annualAfterDiscount), color: "text-white" },
              { label: "Setup Fee", value: fmt(quote.setupFee), color: "text-white" },
              { label: "Year 1 Total", value: fmt(quote.year1Total), color: "text-amber-400" },
            ].map(s => (
              <div key={s.label} className="bg-slate-900 rounded-lg p-3 text-center">
                <div className={`text-lg font-bold font-mono ${s.color}`}>{s.value}</div>
                <div className="text-slate-400 text-xs mt-1">{s.label}</div>
              </div>
            ))}
          </div>

          {/* Unknowns */}
          {unknowns.length > 0 && (
            <div className="bg-amber-900/20 border border-amber-700 rounded-lg p-4">
              <div className="text-amber-400 font-semibold text-sm mb-2">Discovery Questions Still Needed</div>
              <ul className="space-y-1">
                {unknowns.map((u, i) => (
                  <li key={i} className="text-amber-300 text-xs flex items-start gap-1.5">
                    <span className="mt-0.5">&#9654;</span>
                    <span>{u}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Proposal text (collapsible plain text) */}
          <div>
            <div className="text-slate-400 text-xs uppercase tracking-wide mb-2">Plain Text — Copy for Email / CRM</div>
            <pre className="bg-slate-900 rounded-lg p-4 text-slate-300 text-xs leading-relaxed whitespace-pre-wrap max-h-64 overflow-y-auto font-mono border border-slate-700">
              {text}
            </pre>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Proposal text generator ───────────────────────────────────────────────

function generateProposalText(state: WizardState, quote: QuoteResult): string {
  const date = new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
  const lines: string[] = [];

  lines.push("VELARO — PRICING PROPOSAL");
  lines.push("=".repeat(40));
  lines.push(`Date: ${date}`);
  if (state.customer.companyName) lines.push(`Prepared for: ${state.customer.companyName}`);
  if (state.customer.contactName) lines.push(`Contact: ${state.customer.contactName} (${state.customer.contactEmail})`);
  if (state.customer.vertical)    lines.push(`Industry: ${state.customer.vertical}`);
  lines.push("");

  lines.push("MONTHLY INVESTMENT BREAKDOWN");
  lines.push("-".repeat(40));
  quote.lines.forEach(l => {
    const passThrough = l.isPassThrough ? " (pass-through)" : "";
    const note = l.note ? ` — ${l.note}` : "";
    lines.push(`  ${l.label}${passThrough}${note}: ${fmt(l.amount)}/mo`);
  });
  lines.push("");
  lines.push(`  Subtotal: ${fmt(quote.monthlySubtotal)}/mo`);

  if (quote.contractDiscountPct > 0) {
    lines.push(`  ${CONTRACT_LABELS[state.contractTerm]}: -${fmt(quote.monthlySubtotal * quote.contractDiscountPct)}/mo`);
  }
  lines.push(`  Monthly (after discount): ${fmt(quote.monthlyAfterDiscount)}/mo`);
  lines.push("");

  lines.push("TOTAL INVESTMENT");
  lines.push("-".repeat(40));
  lines.push(`  Annual total: ${fmt(quote.annualAfterDiscount)}`);
  lines.push(`  Setup fee: ${fmt(quote.setupFee)}`);
  lines.push(`  Year 1 Total: ${fmt(quote.year1Total)}`);
  lines.push("");

  lines.push("CONTRACT TERMS");
  lines.push("-".repeat(40));
  lines.push(`  Term: ${CONTRACT_LABELS[state.contractTerm]}`);
  lines.push(`  Platform tier: ${quote.tierName} (${quote.tierConversations.toLocaleString()} conversations/mo)`);
  lines.push(`  Channels: ${[
    state.channels.webChat && "Web Chat",
    state.channels.email && "Email",
    state.channels.sms && "SMS",
    state.channels.whatsapp && "WhatsApp",
    state.channels.ivr && "Voice/IVR",
  ].filter(Boolean).join(", ")}`);
  lines.push(`  Journey complexity: ${state.journey.level}`);
  if (state.channels.ivr) {
    lines.push(`  IVR tier: ${IVR_TIERS[state.addons.ivrTier].name} — ${IVR_TIERS[state.addons.ivrTier].blurb}`);
  }
  lines.push("");

  if (quote.ivrFloorWarning) {
    lines.push(`*** NOTE: IVR minimum ($${IVR_FLOOR.toLocaleString()}/mo) not yet met. Review carrier minutes or adjust tier. ***`);
    lines.push("");
  }

  // Commerce section
  if (state.commerce.platform !== "none") {
    const plat = COMMERCE_PLATFORM_LABELS[state.commerce.platform];
    lines.push(`${plat.toUpperCase()} INTEGRATION`);
    lines.push("-".repeat(40));

    if (state.commerce.existingContractBase > 0) {
      lines.push(`  Existing contract: ${fmt(state.commerce.existingContractBase)}/mo (${state.commerce.agentSeats} agents @ ${fmt(state.commerce.perAgentRate)})`);
    }

    const pkgs: string[] = [];
    if (state.commerce.corePackage)      pkgs.push(`Core (${COMMERCE_PACKAGES.core.skills} skills, ${fmt(COMMERCE_PACKAGES.core.marked)}/mo)`);
    if (state.commerce.commercePackage)  pkgs.push(`Commerce (${COMMERCE_PACKAGES.commerce.skills} skills, ${fmt(COMMERCE_PACKAGES.commerce.marked)}/mo)`);
    if (state.commerce.b2bOpsPackage)    pkgs.push(`B2B & Ops (${COMMERCE_PACKAGES.b2bOps.skills} skills, ${fmt(COMMERCE_PACKAGES.b2bOps.marked)}/mo)`);
    if (state.commerce.logisticsPackage) pkgs.push(`Logistics (${COMMERCE_PACKAGES.logistics.skills} skills, ${fmt(COMMERCE_PACKAGES.logistics.marked)}/mo)`);
    if (pkgs.length > 0) {
      lines.push(`  Skill packages: ${pkgs.join(" + ")}`);
    }

    if (state.commerce.botConversationsPerMonth > 0) {
      lines.push(`  Bot conversations: ${state.commerce.botConversationsPerMonth.toLocaleString()}/mo`);
    }
    if (state.commerce.skuCount > 0) {
      lines.push(`  Products indexed: ${state.commerce.skuCount.toLocaleString()} SKUs`);
    }
    if (state.commerce.storefrontCount > 1) {
      lines.push(`  Storefronts: ${state.commerce.storefrontCount}`);
    }
    if (state.commerce.pilotMonths > 0) {
      lines.push(`  *** PILOT: Commerce add-ons waived for first ${state.commerce.pilotMonths} months ***`);
    }
    lines.push("");
  }

  lines.push("NOTE: This is an indicative estimate. Final pricing subject to discovery, contract negotiation, and Velaro legal review.");

  return lines.join("\n");
}

// ── IVR Knowledge Reference Panel ─────────────────────────────────────────

export function IvrKnowledgePanel() {
  const [expanded, setExpanded] = React.useState(false);

  return (
    <div className="bg-slate-800 border border-slate-700 rounded-xl overflow-hidden">
      <button
        className="w-full px-5 py-3 flex items-center justify-between text-left hover:bg-slate-750 transition-colors"
        onClick={() => setExpanded(v => !v)}
        aria-expanded={expanded}
      >
        <div className="flex items-center gap-2">
          <span className="text-blue-400 text-sm font-semibold">IVR &amp; Multilingual — Sales Reference</span>
          <span className="text-xs bg-blue-900/40 text-blue-300 border border-blue-700 rounded px-1.5 py-0.5">Internal</span>
        </div>
        <span className="text-slate-400 text-sm">{expanded ? "▲" : "▼"}</span>
      </button>

      {expanded && (
        <div className="px-5 pb-5 space-y-5 text-xs text-slate-300 border-t border-slate-700 pt-4">

          {/* Pricing model */}
          <section>
            <h3 className="text-white font-semibold text-sm mb-2">IVR All-In Pricing Model</h3>
            <div className="bg-slate-900 rounded p-3 space-y-1">
              <div className="flex justify-between"><span className="text-slate-400">Base platform (IVR)</span><span className="font-mono text-green-400">$3,700/mo</span></div>
              <div className="text-slate-500 text-xs pl-2">Includes: IVR software, ElevenLabs TTS voice, 10,000 min/mo carrier</div>
              <div className="flex justify-between mt-2"><span className="text-slate-400">Additional minutes (per 5K block)</span><span className="font-mono">$250/mo</span></div>
              <div className="flex justify-between"><span className="text-slate-400">Multilingual IVR Standard (3 languages)</span><span className="font-mono">$600/mo</span></div>
              <div className="flex justify-between"><span className="text-slate-400">Multilingual IVR Premium (5 languages)</span><span className="font-mono">$900/mo</span></div>
              <div className="flex justify-between"><span className="text-slate-400">Supervisor Bridging (per 1K bridged min)</span><span className="font-mono">$200</span></div>
            </div>
            <p className="mt-2 text-slate-400">
              The $3,700 base is a hard floor — any deal with IVR must hit this minimum. If the platform tier
              (e.g. Professional at $1,497) is below $3,700, explain the floor and adjust the tier or add volume.
            </p>
          </section>

          {/* ElevenLabs model explanation */}
          <section>
            <h3 className="text-white font-semibold text-sm mb-2">ElevenLabs Voice Models — Which One Are We Using?</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-xs border-collapse">
                <thead>
                  <tr className="border-b border-slate-700">
                    <th className="text-left text-slate-400 py-1 pr-3">Model</th>
                    <th className="text-left text-slate-400 py-1 pr-3">Languages</th>
                    <th className="text-left text-slate-400 py-1 pr-3">Cost/char</th>
                    <th className="text-left text-slate-400 py-1">Latency</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-slate-700/50">
                    <td className="py-1 pr-3 font-mono text-blue-300">eleven_turbo_v2_5</td>
                    <td className="py-1 pr-3">English only</td>
                    <td className="py-1 pr-3 font-mono">$0.000030</td>
                    <td className="py-1 text-green-400">~400ms</td>
                  </tr>
                  <tr className="border-b border-slate-700/50">
                    <td className="py-1 pr-3 font-mono text-blue-300">eleven_multilingual_v2</td>
                    <td className="py-1 pr-3">29 languages</td>
                    <td className="py-1 pr-3 font-mono text-green-400">$0.000030 (same!)</td>
                    <td className="py-1 text-green-400">~400ms</td>
                  </tr>
                  <tr>
                    <td className="py-1 pr-3 font-mono text-slate-500">eleven_flash_v2_5</td>
                    <td className="py-1 pr-3 text-slate-500">English only</td>
                    <td className="py-1 pr-3 font-mono text-slate-500">$0.000015 (cheaper)</td>
                    <td className="py-1 text-slate-500">~250ms</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div className="mt-2 bg-amber-900/20 border border-amber-700 rounded p-2">
              <strong className="text-amber-400">Key insight:</strong> Multilingual_v2 costs the <em>same</em> as turbo.
              We default to multilingual_v2 for all IVR because it covers English callers perfectly and auto-activates
              for non-English callers. There is no price penalty for offering multilingual capability.
            </div>
          </section>

          {/* Multilingual feature explanation */}
          <section>
            <h3 className="text-white font-semibold text-sm mb-2">How Multilingual IVR Works (for sales conversations)</h3>
            <ol className="space-y-1.5 list-decimal list-inside text-slate-400">
              <li>Caller phones the IVR line.</li>
              <li>Deepgram transcribes their speech and detects language automatically.</li>
              <li>Bot responds in the same language the caller used ("respond in caller's language" mode).</li>
              <li>ElevenLabs voices the reply in that language — same turbo quality, same $0.000030/char.</li>
              <li>Agents see the conversation with auto-translation in the inbox.</li>
            </ol>
            <div className="mt-2 text-slate-400">
              <strong className="text-white">Supported languages</strong> include: Spanish, French, German, Italian,
              Portuguese, Polish, Hindi, Japanese, Chinese (Mandarin), Korean, Vietnamese, Dutch, Turkish, Swedish,
              Indonesian, Filipino, Romanian, Ukrainian, Greek, Czech, Finnish, Croatian, Slovak, Arabic.
            </div>
            <div className="mt-2 text-slate-400">
              <strong className="text-white">What the $600/$900 add-on covers:</strong> Subscription gate lift
              (MultilingualLanguageLimit 3 or 5), per-client language selection in the IVR config panel,
              and enabling the "Auto Detect" language setting. The voice TTS cost is already covered by the base.
            </div>
          </section>

          {/* "Respond in caller's language" toggle */}
          <section>
            <h3 className="text-white font-semibold text-sm mb-2">Setting It Up (2 places to configure)</h3>
            <div className="space-y-2">
              <div className="bg-slate-900 rounded p-2">
                <div className="text-white font-medium">1. IVR Voice Configuration → Languages</div>
                <div className="text-slate-400">Check the languages you want to support (English is always on). Limit enforced by subscription.</div>
              </div>
              <div className="bg-slate-900 rounded p-2">
                <div className="text-white font-medium">2. Bot Settings → Language → "Auto Detect"</div>
                <div className="text-slate-400">Set the bot's language to "Auto Detect" — this adds "respond in caller's language" to the AI prompt automatically.</div>
              </div>
            </div>
          </section>

          {/* Margin note */}
          <section>
            <h3 className="text-white font-semibold text-sm mb-2">Margin Reference (M8trix-size customer)</h3>
            <div className="bg-slate-900 rounded p-3 space-y-1 font-mono text-xs">
              <div className="flex justify-between"><span className="text-slate-400">~8,400 calls/mo × 3 min avg = 18.3M chars/mo</span></div>
              <div className="flex justify-between"><span className="text-slate-400">ElevenLabs TTS cost</span><span className="text-red-400">~$550/mo</span></div>
              <div className="flex justify-between"><span className="text-slate-400">Deepgram STT cost</span><span className="text-red-400">~$84/mo</span></div>
              <div className="flex justify-between"><span className="text-slate-400">Twilio carrier (28K min)</span><span className="text-red-400">~$280/mo</span></div>
              <div className="flex justify-between"><span className="text-slate-400">Claude AI cost</span><span className="text-red-400">~$133/mo</span></div>
              <div className="flex justify-between border-t border-slate-700 pt-1"><span className="text-slate-400">Total COGS (approx)</span><span className="text-red-400">~$1,047/mo</span></div>
              <div className="flex justify-between"><span className="text-slate-400">Revenue (at $3,700 base + $250 extra min block)</span><span className="text-green-400">$3,950/mo</span></div>
              <div className="flex justify-between font-bold"><span className="text-white">Gross margin</span><span className="text-green-400">~73%</span></div>
            </div>
            <p className="mt-1 text-slate-500">Multilingual adds no COGS — same voice model price. The $600 add-on is 100% margin.</p>
          </section>

        </div>
      )}
    </div>
  );
}
